// M2V.io - Internationalization
import { ref, computed } from 'vue'

export type Locale = 'fr' | 'en' | 'es' | 'it' | 'de' | 'pt'

export const LOCALES: Record<Locale, { name: string; flag: string }> = {
  fr: { name: 'Français', flag: '🇫🇷' },
  en: { name: 'English', flag: '🇬🇧' },
  es: { name: 'Español', flag: '🇪🇸' },
  it: { name: 'Italiano', flag: '🇮🇹' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  pt: { name: 'Português', flag: '🇵🇹' }
}

const translations: Record<Locale, Record<string, string>> = {
  fr: {
    // Navigation
    'nav.home': 'Accueil',
    'nav.gallery': 'Galerie',
    'nav.pricing': 'Tarifs',
    'nav.dashboard': 'Tableau de bord',
    'nav.login': 'Connexion',
    'nav.logout': 'Déconnexion',
    'nav.profile': 'Profil',
    
    // Footer
    'footer.tagline': 'Créez des vidéos musicales époustouflantes générées par IA',
    'footer.navigation': 'Navigation',
    'footer.support': 'Support',
    'footer.faq': 'FAQ',
    'footer.legal': 'Légal',
    'footer.terms': 'Conditions d\'utilisation',
    'footer.privacy': 'Confidentialité',
    'footer.contact': 'Contact',
    
    // Home
    'home.hero.title': 'Transformez votre musique en vidéo',
    'home.hero.subtitle': 'Créez des vidéos musicales époustouflantes générées par IA à partir de vos pistes audio en quelques minutes',
    'home.hero.cta': 'Commencer',
    'home.hero.watch': 'Voir la galerie',
    'home.demo.watch': 'Voir la démo',
    'home.features.title': 'Comment ça marche',
    'home.features.upload.title': 'Uploadez votre audio',
    'home.features.upload.desc': 'Téléchargez votre fichier MP3, WAV ou tout autre format audio',
    'home.features.ai.title': 'L\'IA crée le script',
    'home.features.ai.desc': 'Notre IA analyse votre audio et génère un scénario visuel unique',
    'home.features.video.title': 'Recevez votre vidéo',
    'home.features.video.desc': 'Obtenez une vidéo musicale professionnelle en HD',
    'home.why.title': 'Pourquoi M2V.io ?',
    'home.why.fast': 'Rapide',
    'home.why.fast.desc': 'Génération en quelques minutes, pas en heures',
    'home.why.quality': 'Qualité HD',
    'home.why.quality.desc': 'Vidéos en haute définition prêtes pour les réseaux',
    'home.why.ai': 'IA Avancée',
    'home.why.ai.desc': 'Les derniers modèles GPT-4, DALL-E 3 et MiniMax',
    'home.why.formats': 'Tous formats',
    'home.why.formats.desc': 'Landscape, Portrait, Carré pour tous vos usages',
    'home.cta.title': 'Prêt à créer votre première vidéo ?',
    'home.cta.subtitle': 'Inscrivez-vous et commencez dès maintenant',
    
    // Gallery
    'gallery.title': 'Galerie publique',
    'gallery.subtitle': 'Découvrez les créations de notre communauté',
    'gallery.empty': 'La galerie est vide pour le moment',
    'gallery.empty.desc': 'Soyez le premier à partager votre création !',
    'gallery.mine': 'Mes vidéos',
    'gallery.filter.all': 'Toutes les langues',
    
    // Pricing
    'pricing.title': 'Tarifs simples et transparents',
    'pricing.subtitle': 'Payez uniquement ce que vous utilisez, sans abonnement',
    'pricing.credit': 'crédit',
    'pricing.credits': 'crédits',
    'pricing.per_credit': 'par crédit',
    'pricing.duration': '30 secondes de vidéo',
    'pricing.buy': 'Acheter',
    'pricing.popular': 'Populaire',
    'pricing.how.title': 'Comment fonctionnent les crédits ?',
    'pricing.how.desc': 'Un crédit vous permet de générer 30 secondes de vidéo. Les crédits ne sont pas divisibles : une vidéo de 35 secondes consommera 2 crédits.',
    'pricing.example': 'Exemple : Pour une chanson de 3 minutes (180 secondes), vous aurez besoin de 6 crédits.',
    'pricing.included.title': 'Inclus avec chaque crédit',
    'pricing.included.script': 'Script généré par IA',
    'pricing.included.images': 'Images haute qualité',
    'pricing.included.video': 'Vidéo HD animée',
    'pricing.included.audio': 'Synchronisation audio',
    'pricing.included.download': 'Téléchargement illimité',
    'pricing.included.formats': 'Tous les formats (16:9, 9:16, 1:1)',
    'pricing.included.no_watermark': 'Aucun watermark',
    'pricing.included.intro_video': 'Vidéo d\'intro personnalisée',
    'pricing.select_credits': 'Choisissez vos crédits',
    'pricing.of_video': 'de vidéo',
    'pricing.over_slider_max': 'Valeur supérieure à {max} crédits',
    
    // Director subscription
    'pricing.director.title': 'Director',
    'pricing.director.description': 'Accédez à tous les fichiers de vos projets',
    'pricing.director.month': 'mois',
    'pricing.director.subscribe': 'S\'abonner à Director',
    'pricing.director.active': 'Abonnement actif',
    'pricing.director.canceling': 'Abonnement en cours d\'annulation',
    'pricing.director.manage': 'Gérer mon abonnement',
    'pricing.director.features.download_assets': 'Télécharger tous les assets du projet',
    'pricing.director.features.script_json': 'Script complet en JSON',
    'pricing.director.features.all_images': 'Toutes les images générées (PNG)',
    'pricing.director.features.all_videos': 'Toutes les vidéos intermédiaires (MP4)',
    'pricing.director.features.zip_archive': 'Archive ZIP complète',
    
    // Terms of Service
    'terms.title': 'Conditions d\'utilisation',
    'terms.last_update': 'Dernière mise à jour',
    'terms.acceptance.title': 'Acceptation des conditions',
    'terms.acceptance.content': 'En accédant et en utilisant M2V.io, vous acceptez d\'être lié par ces conditions d\'utilisation. Si vous n\'acceptez pas ces conditions, veuillez ne pas utiliser notre service.',
    'terms.service.title': 'Description du service',
    'terms.service.content': 'M2V.io est une plateforme de génération de vidéos musicales assistée par intelligence artificielle. Notre service permet aux utilisateurs de :',
    'terms.service.item1': 'Télécharger des fichiers audio',
    'terms.service.item2': 'Générer des scripts visuels par IA',
    'terms.service.item3': 'Créer des vidéos animées à partir d\'images générées',
    'terms.service.item4': 'Télécharger et partager les vidéos créées',
    'terms.credits.title': 'Système de crédits',
    'terms.credits.content': 'L\'utilisation du service nécessite des crédits. Les conditions suivantes s\'appliquent :',
    'terms.credits.item1': 'Un crédit permet de générer 30 secondes de vidéo',
    'terms.credits.item2': 'Les crédits ne sont pas divisibles et ne sont pas remboursables',
    'terms.credits.item3': 'Les crédits n\'expirent pas et restent liés à votre compte',
    'terms.content.title': 'Contenu utilisateur',
    'terms.content.content': 'Vous êtes responsable du contenu que vous téléchargez. Il est interdit de télécharger :',
    'terms.content.item1': 'Du contenu protégé par des droits d\'auteur sans autorisation',
    'terms.content.item2': 'Du contenu illégal, diffamatoire ou offensant',
    'terms.content.item3': 'Du contenu contenant des virus ou logiciels malveillants',
    'terms.intellectual.title': 'Propriété intellectuelle',
    'terms.intellectual.content': 'Vous conservez tous les droits sur le contenu audio que vous téléchargez. Les vidéos générées vous appartiennent et vous pouvez les utiliser librement à des fins personnelles ou commerciales. M2V.io conserve le droit d\'utiliser les vidéos publiées dans la galerie publique à des fins promotionnelles.',
    'terms.liability.title': 'Limitation de responsabilité',
    'terms.liability.content': 'M2V.io est fourni "tel quel" sans garantie d\'aucune sorte. Nous ne sommes pas responsables des dommages directs ou indirects résultant de l\'utilisation de notre service. Les résultats de la génération IA peuvent varier et ne sont pas garantis.',
    'terms.termination.title': 'Résiliation',
    'terms.termination.content': 'Nous nous réservons le droit de suspendre ou de résilier votre compte en cas de violation de ces conditions. En cas de résiliation, vous perdrez l\'accès à votre compte mais conserverez les vidéos déjà téléchargées.',
    'terms.modifications.title': 'Modifications',
    'terms.modifications.content': 'Nous nous réservons le droit de modifier ces conditions à tout moment. Les modifications seront effectives dès leur publication sur cette page. Votre utilisation continue du service après modification constitue votre acceptation des nouvelles conditions.',
    'terms.contact.title': 'Contact',
    'terms.contact.content': 'Pour toute question concernant ces conditions d\'utilisation, veuillez nous contacter à :',
    
    // Privacy Policy
    'privacy.title': 'Politique de confidentialité',
    'privacy.last_update': 'Dernière mise à jour',
    'privacy.intro.title': 'Introduction',
    'privacy.intro.content': 'M2V.io s\'engage à protéger votre vie privée. Cette politique explique comment nous collectons, utilisons et protégeons vos données personnelles conformément au RGPD.',
    'privacy.collected.title': 'Données collectées',
    'privacy.collected.content': 'Nous collectons les données suivantes :',
    'privacy.collected.item1': 'Informations de compte : email, nom d\'affichage, photo de profil (via Google/Facebook)',
    'privacy.collected.item2': 'Données d\'utilisation : projets créés, fichiers téléchargés, préférences',
    'privacy.collected.item3': 'Données de paiement : traitées par Stripe, nous ne stockons pas vos informations de carte',
    'privacy.collected.item4': 'Données techniques : adresse IP, type de navigateur, logs de connexion',
    'privacy.usage.title': 'Utilisation des données',
    'privacy.usage.content': 'Vos données sont utilisées pour :',
    'privacy.usage.item1': 'Fournir et améliorer nos services',
    'privacy.usage.item2': 'Traiter vos paiements et gérer votre compte',
    'privacy.usage.item3': 'Vous envoyer des communications importantes (pas de spam)',
    'privacy.usage.item4': 'Analyser l\'utilisation pour améliorer le service',
    'privacy.storage.title': 'Conservation des données',
    'privacy.storage.content': 'Vos données personnelles sont conservées tant que votre compte est actif. Les fichiers audio et vidéos sont stockés pendant 12 mois après création. Vous pouvez demander la suppression de vos données à tout moment.',
    'privacy.sharing.title': 'Partage des données',
    'privacy.sharing.content': 'Nous ne vendons jamais vos données. Nous les partageons uniquement avec :',
    'privacy.sharing.item1': 'Nos prestataires techniques (hébergement, paiement) sous contrat de confidentialité',
    'privacy.sharing.item2': 'Les autorités si requis par la loi',
    'privacy.cookies.title': 'Cookies',
    'privacy.cookies.content': 'Nous utilisons des cookies essentiels pour le fonctionnement du site (authentification, préférences). Nous n\'utilisons pas de cookies de tracking publicitaire.',
    'privacy.rights.title': 'Vos droits',
    'privacy.rights.content': 'Conformément au RGPD, vous disposez des droits suivants :',
    'privacy.rights.item1': 'Droit d\'accès : obtenir une copie de vos données',
    'privacy.rights.item2': 'Droit de rectification : corriger vos données',
    'privacy.rights.item3': 'Droit à l\'effacement : supprimer vos données',
    'privacy.rights.item4': 'Droit à la portabilité : exporter vos données',
    'privacy.security.title': 'Sécurité',
    'privacy.security.content': 'Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos données : chiffrement HTTPS, authentification sécurisée, accès restreint aux données.',
    'privacy.contact.title': 'Contact DPO',
    'privacy.contact.content': 'Pour exercer vos droits ou pour toute question relative à la protection de vos données, contactez notre délégué à la protection des données :',
    
    // Contact
    'contact.title': 'Contactez-nous',
    'contact.subtitle': 'Une question, une suggestion ? Nous sommes là pour vous aider.',
    'contact.response_time': 'Temps de réponse',
    'contact.response_delay': 'Sous 24-48h ouvrées',
    'contact.languages': 'Langues supportées',
    'contact.form.name': 'Nom',
    'contact.form.name_placeholder': 'Votre nom',
    'contact.form.email': 'Email',
    'contact.form.email_placeholder': 'votre@email.com',
    'contact.form.subject': 'Sujet',
    'contact.form.subject_placeholder': 'Sélectionnez un sujet',
    'contact.form.message': 'Message',
    'contact.form.message_placeholder': 'Décrivez votre demande en détail...',
    'contact.form.captcha': 'Vérification anti-spam',
    'contact.form.captcha_hint': 'Résolvez ce calcul simple pour prouver que vous êtes humain.',
    'contact.form.captcha_placeholder': 'Réponse',
    'contact.form.captcha_refresh': 'Nouveau calcul',
    'contact.form.send': 'Envoyer le message',
    'contact.form.invalid': 'Veuillez remplir tous les champs correctement',
    'contact.form.success': 'Message envoyé avec succès !',
    'contact.form.error': 'Erreur lors de l\'envoi, veuillez réessayer',
    'contact.form.sent_title': 'Message envoyé !',
    'contact.form.sent_message': 'Nous avons bien reçu votre message et vous répondrons dans les plus brefs délais.',
    'contact.form.new_message': 'Envoyer un autre message',
    'contact.subject.general': 'Question générale',
    'contact.subject.support': 'Support technique',
    'contact.subject.billing': 'Facturation',
    'contact.subject.partnership': 'Partenariat',
    'contact.subject.other': 'Autre',
    
    // FAQ
    'faq.title': 'Foire aux questions',
    'faq.subtitle': 'Trouvez rapidement les réponses à vos questions les plus fréquentes',
    'faq.q1.question': 'Combien de temps pour générer une vidéo de 3 minutes ?',
    'faq.q1.answer': 'Il faut prévoir environ 40 minutes pour obtenir la vidéo en mode automatique.',
    'faq.q2.question': 'Quelle est la différence entre le mode automatique et manuel ?',
    'faq.q2.answer_intro': 'Les deux modes offrent des niveaux de contrôle différents :',
    'faq.q2.auto_title': 'Mode automatique',
    'faq.q2.auto_desc': 'Vous suivez la progression de la génération sans aucune intervention de votre part.',
    'faq.q2.manual_title': 'Mode manuel',
    'faq.q2.manual_desc': 'Vous pouvez modifier et valider le script, visualiser les images de chaque scène et en modifier 3 au maximum, et enfin visualiser les vidéos de chaque scène, changer l\'ordre et régénérer au maximum 3 vidéos.',
    'faq.q3.question': 'À quoi correspond l\'abonnement supplémentaire "Director" ?',
    'faq.q3.answer': 'En plus de télécharger la vidéo finale, vous pourrez télécharger l\'ensemble des éléments qui ont constitué la vidéo :',
    'faq.q3.item1': 'Le script complet du projet',
    'faq.q3.item2': 'Toutes les images générées pour chaque scène',
    'faq.q3.item3': 'L\'ensemble des vidéos intermédiaires',
    'faq.q4.question': 'Quel format vidéo puis-je produire ?',
    'faq.q4.answer': 'Il est possible de faire des vidéos au format 16:9 (paysage), 9:16 (portrait) ou 1:1 (carré). Le fichier fourni est au format MP4.',
    'faq.q5.question': 'Est-il possible d\'annuler un projet ?',
    'faq.q5.answer': 'Oui, mais en fonction de l\'étape en cours au moment de l\'annulation, des frais seront prélevés : 1 crédit si vous annulez avant la génération des vidéos, et si des vidéos ont déjà été générées, c\'est la durée des vidéos générées qui fixera le nombre de crédits consommés.',
    'faq.q6.question': 'Un projet peut-il être récupéré si je l\'ai supprimé ?',
    'faq.q6.answer': 'Non, les projets supprimés ne sont pas récupérables. Assurez-vous de télécharger tous les fichiers souhaités avant de supprimer un projet.',
    'faq.more_questions': 'Vous avez d\'autres questions ?',
    'faq.contact_us': 'Notre équipe est là pour vous aider.',
    'faq.contact_button': 'Nous contacter',
    
    // Dashboard
    'dashboard.title': 'Tableau de bord',
    'dashboard.welcome': 'Bienvenue',
    'dashboard.credits': 'Crédits disponibles',
    'dashboard.projects': 'Mes projets',
    'dashboard.new_project': 'Nouveau projet',
    'dashboard.no_projects': 'Aucun projet',
    'dashboard.create_first': 'Créez votre première vidéo musicale !',
    'dashboard.payment_success': 'Paiement effectué avec succès ! Vos crédits ont été ajoutés.',
    
    // Project
    'project.title': 'Titre du projet',
    'project.language': 'Langue',
    'project.format': 'Format vidéo',
    'project.format.landscape': 'Paysage (16:9)',
    'project.format.portrait': 'Portrait (9:16)',
    'project.format.square': 'Carré (1:1)',
    'project.style': 'Style visuel',
    'project.mood': 'Ambiance / Description',
    'project.audio': 'Fichier audio',
    'project.srt': 'Sous-titres (optionnel)',
    'project.intro': 'Intro vidéo (optionnel)',
    'project.outro': 'Outro vidéo (optionnel)',
    'project.photo': 'Photo personnage (optionnel)',
    'project.mode': 'Mode de création',
    'project.mode.auto': 'Automatique',
    'project.mode.manual': 'Manuel (étape par étape)',
    'project.public': 'Publier dans la galerie',
    'project.create': 'Créer le projet',
    'project.no_credits': 'Vous n\'avez pas de crédits. Veuillez en acheter pour créer un projet.',
    'project.error_no_audio': 'Veuillez sélectionner un fichier audio',
    'project.error_no_title': 'Veuillez entrer un titre',
    'project.start': 'Lancer la génération',
    'project.cancel': 'Annuler',
    'project.delete': 'Supprimer',
    'project.download_assets': 'Télécharger les assets',
    'project.download_assets_desc': 'Script, images et vidéos (ZIP)',
    'project.download_assets_success': 'Archive créée avec succès',
    'project.download_assets_error': 'Erreur lors de la création de l\'archive',
    'project.director_required': 'Abonnement Director requis',
    'project.director_required_desc': 'Abonnez-vous à Director pour télécharger tous les fichiers du projet',
    'project.active_warning.title': 'Projet en cours',
    'project.active_warning.message': 'Vous avez déjà un projet en cours de génération : "{title}". Vous ne pouvez pas lancer un nouveau projet tant que celui-ci n\'est pas terminé.',
    'project.active_warning.view_project': 'Voir le projet en cours',
    
    // Script Editor
    'script.title': 'Éditeur de script',
    'script.scenes': 'scènes',
    'script.total_duration': 'durée totale',
    'script.add_scene': 'Ajouter une scène',
    'script.validate_and_generate': 'Valider et générer les images',
    'script.description': 'Description',
    'script.image_prompt': 'Prompt image',
    'script.video_prompt': 'Prompt vidéo',
    'script.duration_s': 'Durée (secondes)',
    'script.edit_scene': 'Modifier la scène',
    'script.description_placeholder': 'Description narrative de la scène...',
    'script.image_prompt_placeholder': 'Prompt détaillé pour la génération d\'image (en anglais)...',
    'script.video_prompt_placeholder': 'Prompt détaillé pour l\'animation vidéo (en anglais)...',
    'script.scene_updated': 'Scène mise à jour',
    'script.scene_added': 'Scène ajoutée',
    'script.scene_deleted': 'Scène supprimée',
    'script.error_update': 'Erreur lors de la mise à jour',
    'script.error_create': 'Erreur lors de la création',
    'script.fill_all_fields': 'Veuillez remplir tous les champs obligatoires',
    'script.confirm_delete_title': 'Supprimer la scène',
    'script.confirm_delete_message': 'Êtes-vous sûr de vouloir supprimer cette scène ?',
    'script.confirm_validate_title': 'Valider le script',
    'script.confirm_validate_message': 'Valider le script lancera la génération des images. Cette action consommera des crédits. Continuer ?',
    'script.validate': 'Valider',
    'script.add': 'Ajouter',
    'script.no_scenes': 'Aucune scène dans le script',
    'script.awaiting_validation': 'Le script est prêt. Modifiez-le si nécessaire puis validez pour lancer la génération des images.',
    
    // Image Validation
    'images.title': 'Validation des images',
    'images.images': 'images',
    'images.regenerations_left': 'régénérations restantes',
    'images.instructions': 'Cliquez sur une image pour la modifier, la régénérer ou la remplacer. Validez ensuite pour lancer la génération des vidéos.',
    'images.click_to_edit': 'Cliquer pour modifier',
    'images.edit_image': 'Modifier l\'image',
    'images.no_image': 'Pas d\'image',
    'images.regenerate': 'Régénérer',
    'images.remaining': 'restantes',
    'images.regeneration_limit_reached': 'Limite de régénération atteinte (maximum 3)',
    'images.prompt_required': 'Le prompt image (en anglais) est requis',
    'images.upload_replacement': 'Uploader une image',
    'images.upload_hint': 'Aucune limite sur les uploads',
    'images.confirm_regenerate_title': 'Régénérer l\'image',
    'images.confirm_regenerate_message': 'Voulez-vous régénérer cette image ? Il vous reste {remaining} régénération(s).',
    'images.regeneration_started': 'Régénération lancée',
    'images.invalid_file_type': 'Type de fichier invalide. Veuillez uploader une image.',
    'images.file_too_large': 'Fichier trop volumineux (max 10 Mo)',
    'images.upload_success': 'Image uploadée avec succès',
    'images.upload_error': 'Erreur lors de l\'upload',
    'images.confirm_validate_title': 'Valider les images',
    'images.confirm_validate_message': 'Valider les images lancera la génération des vidéos. Cette action consommera des crédits. Continuer ?',
    'images.validate_and_generate': 'Valider et générer les vidéos',
    'images.awaiting_validation': 'Les images sont prêtes. Vérifiez-les et modifiez-les si nécessaire, puis validez pour lancer la génération des vidéos.',
    
    // Video Validation
    'videos.title': 'Validation des vidéos',
    'videos.videos': 'vidéos',
    'videos.regenerations_left': 'régénérations restantes',
    'videos.instructions': 'Cliquez sur une vidéo pour la modifier ou la régénérer. Utilisez les flèches pour réorganiser l\'ordre des scènes. Validez ensuite pour lancer la fusion finale.',
    'videos.click_to_edit': 'Cliquer pour modifier',
    'videos.edit_video': 'Modifier la vidéo',
    'videos.no_video': 'Pas de vidéo',
    'videos.regenerate': 'Régénérer',
    'videos.remaining': 'restantes',
    'videos.regeneration_limit_reached': 'Limite de régénération atteinte (maximum 3)',
    'videos.confirm_regenerate_title': 'Régénérer la vidéo',
    'videos.confirm_regenerate_message': 'Voulez-vous régénérer cette vidéo ? Il vous reste {remaining} régénération(s).',
    'videos.regeneration_started': 'Régénération lancée',
    'videos.scene_number': 'Numéro de scène',
    'videos.duration': 'Durée',
    'videos.move_left': 'Déplacer vers la gauche',
    'videos.move_right': 'Déplacer vers la droite',
    'videos.scenes_swapped': 'Scènes échangées',
    'videos.confirm_validate_title': 'Valider les vidéos',
    'videos.confirm_validate_message': 'Valider les vidéos lancera la fusion finale et l\'ajout de l\'audio. Continuer ?',
    'videos.validate_and_merge': 'Valider et fusionner',
    'videos.awaiting_validation': 'Les vidéos sont prêtes. Réorganisez l\'ordre si nécessaire, puis validez pour lancer la fusion finale avec l\'audio.',
    
    // Status (Project)
    'status.draft': 'Brouillon',
    'status.pending': 'En attente',
    'status.processing': 'En cours',
    'status.script_ready': 'Script prêt',
    'status.images_ready': 'Images prêtes',
    'status.videos_ready': 'Vidéos prêtes',
    'status.completed': 'Terminé',
    'status.failed': 'Échec',
    'status.cancelled': 'Annulé',
    // Status (Scene)
    'status.image_generating': 'Génération image...',
    'status.image_ready': 'Image prête',
    'status.video_generating': 'Génération vidéo...',
    'status.video_ready': 'Vidéo prête',
    
    // Wizard
    'wizard.step1': 'Script',
    'wizard.step2': 'Images',
    'wizard.step3': 'Vidéos',
    'wizard.step4': 'Fusion',
    'wizard.step5': 'Audio',
    'wizard.validate': 'Valider et continuer',
    'wizard.regenerate': 'Régénérer',
    'wizard.edit': 'Modifier',
    
    // Auth
    'auth.login_with_google': 'Continuer avec Google',
    'auth.login_with_facebook': 'Continuer avec Facebook',
    'auth.or': 'ou',
    'auth.dev_login': 'Connexion développeur',
    
    // Profile
    'profile.title': 'Mon profil',
    'profile.display_name': 'Nom affiché',
    'profile.language': 'Langue',
    'profile.theme': 'Thème',
    'profile.theme.dark': 'Sombre',
    'profile.theme.light': 'Clair',
    'profile.save': 'Enregistrer',
    'profile.saved': 'Enregistré',
    'profile.transactions': 'Historique des transactions',
    'profile.no_transactions': 'Aucune transaction',
    'profile.subscription.title': 'Abonnement Director',
    'profile.subscription.active': 'Abonnement actif',
    'profile.subscription.canceling': 'Annulation programmée',
    'profile.subscription.renews_on': 'Se renouvelle le {date}',
    'profile.subscription.ends_on': 'Se termine le {date}',
    'profile.subscription.cancel': 'Annuler l\'abonnement',
    'profile.subscription.cancel_title': 'Annuler l\'abonnement ?',
    'profile.subscription.cancel_confirm': 'Êtes-vous sûr de vouloir annuler votre abonnement Director ? Vous conserverez l\'accès jusqu\'à la fin de la période en cours.',
    'profile.subscription.confirm_cancel': 'Oui, annuler',
    'profile.subscription.cancelled': 'Abonnement annulé',
    'profile.subscription.cancel_error': 'Erreur lors de l\'annulation',
    'profile.subscription.reactivate': 'Réactiver l\'abonnement',
    'profile.subscription.reactivated': 'Abonnement réactivé',
    'profile.subscription.reactivate_error': 'Erreur lors de la réactivation',
    'profile.subscription.manage_payment': 'Gérer le paiement',
    'profile.subscription.portal_error': 'Erreur d\'accès au portail',
    'profile.subscription.not_subscribed': 'Vous n\'êtes pas encore abonné à Director',
    'profile.subscription.subscribe_now': 'S\'abonner maintenant',
    'profile.subscription_success': 'Abonnement Director activé avec succès !',
    'profile.billing.title': 'Facturation',
    'profile.billing.description': 'Accédez à vos factures, tickets CB et gérez vos moyens de paiement.',
    'profile.billing.access_portal': 'Mon compte Stripe',
    'profile.billing.portal_error': 'Impossible d\'accéder au portail de facturation',
    
    // Common
    'common.loading': 'Chargement...',
    'common.error': 'Une erreur est survenue',
    'common.retry': 'Réessayer',
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.delete': 'Supprimer',
    'common.confirm': 'Confirmer',
    'common.back': 'Retour',
    'common.next': 'Suivant',
    'common.download': 'Télécharger',
    'common.share': 'Partager',
    'common.close': 'Fermer',
    'common.edit': 'Modifier',
    
    // Project View
    'project.not_found': 'Projet non trouvé',
    'project.back_to_dashboard': 'Retour au tableau de bord',
    'project.processing': 'Traitement en cours... {progress}%',
    'project.your_video': 'Votre vidéo',
    'project.scenes_count': 'Scènes ({count})',
    'project.scene_number': 'Scène {number}',
    'project.confirm_delete_title': 'Confirmer la suppression',
    'project.confirm_delete_message': 'Êtes-vous sûr de vouloir supprimer ce projet ? Cette action est irréversible.',
    // Project Create
    'project.create_subtitle': 'Créez votre vidéo musicale en quelques clics',
    'project.drop_audio': 'Glissez votre fichier audio ici',
    'project.audio_formats': 'MP3, WAV, OGG...',
    'project.section_info': 'Informations',
    'project.section_mode': 'Mode de création',
    'project.section_files': 'Fichiers optionnels',
    'project.mode.auto_desc': 'L\'IA génère tout automatiquement',
    'project.mode.manual_desc': 'Contrôlez chaque étape de la création',
    'project.add_subtitles': 'Ajouter sous-titres',
    'project.add_photo': 'Ajouter photo personnage',
    'project.add_intro': 'Ajouter intro vidéo',
    'project.add_outro': 'Ajouter outro vidéo',
    'project.available_credits': 'Crédits disponibles',
    
    // Profile
    'profile.section_info': 'Informations',
  },
  
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.gallery': 'Gallery',
    'nav.pricing': 'Pricing',
    'nav.dashboard': 'Dashboard',
    'nav.login': 'Login',
    'nav.logout': 'Logout',
    'nav.profile': 'Profile',
    
    // Footer
    'footer.tagline': 'Create stunning AI-generated music videos',
    'footer.navigation': 'Navigation',
    'footer.support': 'Support',
    'footer.faq': 'FAQ',
    'footer.legal': 'Legal',
    'footer.terms': 'Terms of Service',
    'footer.privacy': 'Privacy Policy',
    'footer.contact': 'Contact',
    
    // Home
    'home.hero.title': 'Transform your music into video',
    'home.hero.subtitle': 'Create stunning AI-generated music videos from your audio tracks in minutes',
    'home.hero.cta': 'Get started',
    'home.hero.watch': 'View gallery',
    'home.demo.watch': 'Watch demo',
    'home.features.title': 'How it works',
    'home.features.upload.title': 'Upload your audio',
    'home.features.upload.desc': 'Upload your MP3, WAV or any audio format',
    'home.features.ai.title': 'AI creates the script',
    'home.features.ai.desc': 'Our AI analyzes your audio and generates a unique visual scenario',
    'home.features.video.title': 'Get your video',
    'home.features.video.desc': 'Receive a professional HD music video',
    'home.why.title': 'Why M2V.io?',
    'home.why.fast': 'Fast',
    'home.why.fast.desc': 'Generation in minutes, not hours',
    'home.why.quality': 'HD Quality',
    'home.why.quality.desc': 'High definition videos ready for social media',
    'home.why.ai': 'Advanced AI',
    'home.why.ai.desc': 'Latest GPT-4, DALL-E 3 and MiniMax models',
    'home.why.formats': 'All formats',
    'home.why.formats.desc': 'Landscape, Portrait, Square for all your needs',
    'home.cta.title': 'Ready to create your first video?',
    'home.cta.subtitle': 'Sign up and start now',
    
    // Gallery
    'gallery.title': 'Public Gallery',
    'gallery.subtitle': 'Discover creations from our community',
    'gallery.empty': 'The gallery is empty for now',
    'gallery.empty.desc': 'Be the first to share your creation!',
    'gallery.mine': 'My videos',
    'gallery.filter.all': 'All languages',
    
    // Pricing
    'pricing.title': 'Simple and transparent pricing',
    'pricing.subtitle': 'Pay only for what you use, no subscription',
    'pricing.credit': 'credit',
    'pricing.credits': 'credits',
    'pricing.per_credit': 'per credit',
    'pricing.duration': '30 seconds of video',
    'pricing.buy': 'Buy',
    'pricing.popular': 'Popular',
    'pricing.how.title': 'How do credits work?',
    'pricing.how.desc': 'One credit allows you to generate 30 seconds of video. Credits are not divisible: a 35-second video will consume 2 credits.',
    'pricing.example': 'Example: For a 3-minute song (180 seconds), you will need 6 credits.',
    'pricing.included.title': 'Included with each credit',
    'pricing.included.script': 'AI-generated script',
    'pricing.included.images': 'High quality images',
    'pricing.included.video': 'Animated HD video',
    'pricing.included.audio': 'Audio synchronization',
    'pricing.included.download': 'Unlimited download',
    'pricing.included.formats': 'All formats (16:9, 9:16, 1:1)',
    'pricing.included.no_watermark': 'No watermark',
    'pricing.included.intro_video': 'Custom intro video',
    'pricing.select_credits': 'Choose your credits',
    'pricing.of_video': 'of video',
    'pricing.over_slider_max': 'Value greater than {max} credits',
    
    // Director subscription
    'pricing.director.title': 'Director',
    'pricing.director.description': 'Access all files from your projects',
    'pricing.director.month': 'month',
    'pricing.director.subscribe': 'Subscribe to Director',
    'pricing.director.active': 'Active subscription',
    'pricing.director.canceling': 'Subscription canceling',
    'pricing.director.manage': 'Manage my subscription',
    'pricing.director.features.download_assets': 'Download all project assets',
    'pricing.director.features.script_json': 'Complete script in JSON',
    'pricing.director.features.all_images': 'All generated images (PNG)',
    'pricing.director.features.all_videos': 'All intermediate videos (MP4)',
    'pricing.director.features.zip_archive': 'Complete ZIP archive',
    
    // Terms
    'terms.title': 'Terms of Service',
    'terms.last_update': 'Last updated',
    'terms.acceptance.title': 'Acceptance of terms',
    'terms.acceptance.content': 'By accessing and using M2V.io, you agree to be bound by these terms of service. If you do not accept these terms, please do not use our service.',
    'terms.service.title': 'Service description',
    'terms.service.content': 'M2V.io is an AI-assisted music video generation platform. Our service allows users to:',
    'terms.service.item1': 'Upload audio files',
    'terms.service.item2': 'Generate visual scripts using AI',
    'terms.service.item3': 'Create animated videos from generated images',
    'terms.service.item4': 'Download and share created videos',
    'terms.credits.title': 'Credit system',
    'terms.credits.content': 'Using the service requires credits. The following conditions apply:',
    'terms.credits.item1': 'One credit generates 30 seconds of video',
    'terms.credits.item2': 'Credits are not divisible and non-refundable',
    'terms.credits.item3': 'Credits do not expire and remain linked to your account',
    'terms.content.title': 'User content',
    'terms.content.content': 'You are responsible for the content you upload. It is prohibited to upload:',
    'terms.content.item1': 'Copyrighted content without authorization',
    'terms.content.item2': 'Illegal, defamatory or offensive content',
    'terms.content.item3': 'Content containing viruses or malware',
    'terms.intellectual.title': 'Intellectual property',
    'terms.intellectual.content': 'You retain all rights to the audio content you upload. Generated videos belong to you and you can use them freely for personal or commercial purposes. M2V.io reserves the right to use videos published in the public gallery for promotional purposes.',
    'terms.liability.title': 'Limitation of liability',
    'terms.liability.content': 'M2V.io is provided "as is" without warranty of any kind. We are not responsible for direct or indirect damages resulting from the use of our service. AI generation results may vary and are not guaranteed.',
    'terms.termination.title': 'Termination',
    'terms.termination.content': 'We reserve the right to suspend or terminate your account in case of violation of these terms. In case of termination, you will lose access to your account but will keep videos already downloaded.',
    'terms.modifications.title': 'Modifications',
    'terms.modifications.content': 'We reserve the right to modify these terms at any time. Modifications will be effective upon publication on this page. Your continued use of the service after modification constitutes acceptance of the new terms.',
    'terms.contact.title': 'Contact',
    'terms.contact.content': 'For any questions regarding these terms of service, please contact us at:',
    
    // Privacy
    'privacy.title': 'Privacy Policy',
    'privacy.last_update': 'Last updated',
    'privacy.intro.title': 'Introduction',
    'privacy.intro.content': 'M2V.io is committed to protecting your privacy. This policy explains how we collect, use and protect your personal data in accordance with GDPR.',
    'privacy.collected.title': 'Data collected',
    'privacy.collected.content': 'We collect the following data:',
    'privacy.collected.item1': 'Account information: email, display name, profile picture (via Google/Facebook)',
    'privacy.collected.item2': 'Usage data: projects created, uploaded files, preferences',
    'privacy.collected.item3': 'Payment data: processed by Stripe, we do not store your card information',
    'privacy.collected.item4': 'Technical data: IP address, browser type, connection logs',
    'privacy.usage.title': 'Data usage',
    'privacy.usage.content': 'Your data is used to:',
    'privacy.usage.item1': 'Provide and improve our services',
    'privacy.usage.item2': 'Process your payments and manage your account',
    'privacy.usage.item3': 'Send you important communications (no spam)',
    'privacy.usage.item4': 'Analyze usage to improve the service',
    'privacy.storage.title': 'Data retention',
    'privacy.storage.content': 'Your personal data is retained as long as your account is active. Audio and video files are stored for 12 months after creation. You can request deletion of your data at any time.',
    'privacy.sharing.title': 'Data sharing',
    'privacy.sharing.content': 'We never sell your data. We only share it with:',
    'privacy.sharing.item1': 'Our technical providers (hosting, payment) under confidentiality agreement',
    'privacy.sharing.item2': 'Authorities if required by law',
    'privacy.cookies.title': 'Cookies',
    'privacy.cookies.content': 'We use essential cookies for site operation (authentication, preferences). We do not use advertising tracking cookies.',
    'privacy.rights.title': 'Your rights',
    'privacy.rights.content': 'Under GDPR, you have the following rights:',
    'privacy.rights.item1': 'Right of access: obtain a copy of your data',
    'privacy.rights.item2': 'Right of rectification: correct your data',
    'privacy.rights.item3': 'Right to erasure: delete your data',
    'privacy.rights.item4': 'Right to portability: export your data',
    'privacy.security.title': 'Security',
    'privacy.security.content': 'We implement appropriate security measures to protect your data: HTTPS encryption, secure authentication, restricted data access.',
    'privacy.contact.title': 'DPO Contact',
    'privacy.contact.content': 'To exercise your rights or for any question regarding the protection of your data, contact our data protection officer:',
    
    // Contact
    'contact.title': 'Contact us',
    'contact.subtitle': 'A question, a suggestion? We are here to help.',
    'contact.response_time': 'Response time',
    'contact.response_delay': 'Within 24-48 business hours',
    'contact.languages': 'Supported languages',
    'contact.form.name': 'Name',
    'contact.form.name_placeholder': 'Your name',
    'contact.form.email': 'Email',
    'contact.form.email_placeholder': 'your@email.com',
    'contact.form.subject': 'Subject',
    'contact.form.subject_placeholder': 'Select a subject',
    'contact.form.message': 'Message',
    'contact.form.message_placeholder': 'Describe your request in detail...',
    'contact.form.captcha': 'Anti-spam verification',
    'contact.form.captcha_hint': 'Solve this simple calculation to prove you are human.',
    'contact.form.captcha_placeholder': 'Answer',
    'contact.form.captcha_refresh': 'New calculation',
    'contact.form.send': 'Send message',
    'contact.form.invalid': 'Please fill in all fields correctly',
    'contact.form.success': 'Message sent successfully!',
    'contact.form.error': 'Error sending, please try again',
    'contact.form.sent_title': 'Message sent!',
    'contact.form.sent_message': 'We have received your message and will respond as soon as possible.',
    'contact.form.new_message': 'Send another message',
    'contact.subject.general': 'General question',
    'contact.subject.support': 'Technical support',
    'contact.subject.billing': 'Billing',
    'contact.subject.partnership': 'Partnership',
    'contact.subject.other': 'Other',
    
    // FAQ
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Quickly find answers to your most common questions',
    'faq.q1.question': 'How long does it take to generate a 3-minute video?',
    'faq.q1.answer': 'You should expect about 40 minutes to get the video in automatic mode.',
    'faq.q2.question': 'What is the difference between automatic and manual mode?',
    'faq.q2.answer_intro': 'Both modes offer different levels of control:',
    'faq.q2.auto_title': 'Automatic mode',
    'faq.q2.auto_desc': 'You follow the progress of the generation without any intervention on your part.',
    'faq.q2.manual_title': 'Manual mode',
    'faq.q2.manual_desc': 'You can edit and validate the script, view the images of each scene and modify up to 3, and finally view the videos of each scene, change the order and regenerate up to 3 videos.',
    'faq.q3.question': 'What does the additional "Director" subscription include?',
    'faq.q3.answer': 'In addition to downloading the final video, you will be able to download all the elements that made up the video:',
    'faq.q3.item1': 'The complete project script',
    'faq.q3.item2': 'All images generated for each scene',
    'faq.q3.item3': 'All intermediate videos',
    'faq.q4.question': 'What video format can I produce?',
    'faq.q4.answer': 'You can create videos in 16:9 (landscape), 9:16 (portrait) or 1:1 (square) format. The output file is in MP4 format.',
    'faq.q5.question': 'Is it possible to cancel a project?',
    'faq.q5.answer': 'Yes, but depending on the stage at the time of cancellation, fees will be charged: 1 credit if you cancel before video generation, and if videos have already been generated, it is the duration of the generated videos that will determine the number of credits consumed.',
    'faq.q6.question': 'Can a project be recovered if I deleted it?',
    'faq.q6.answer': 'No, deleted projects cannot be recovered. Make sure to download all desired files before deleting a project.',
    'faq.more_questions': 'Do you have more questions?',
    'faq.contact_us': 'Our team is here to help you.',
    'faq.contact_button': 'Contact us',
    
    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Welcome',
    'dashboard.credits': 'Available credits',
    'dashboard.projects': 'My projects',
    'dashboard.new_project': 'New project',
    'dashboard.no_projects': 'No projects',
    'dashboard.create_first': 'Create your first music video!',
    'dashboard.payment_success': 'Payment successful! Your credits have been added.',
    
    // Project
    'project.title': 'Project title',
    'project.language': 'Language',
    'project.format': 'Video format',
    'project.format.landscape': 'Landscape (16:9)',
    'project.format.portrait': 'Portrait (9:16)',
    'project.format.square': 'Square (1:1)',
    'project.style': 'Visual style',
    'project.mood': 'Mood / Description',
    'project.audio': 'Audio file',
    'project.srt': 'Subtitles (optional)',
    'project.intro': 'Video intro (optional)',
    'project.outro': 'Video outro (optional)',
    'project.photo': 'Character photo (optional)',
    'project.mode': 'Creation mode',
    'project.mode.auto': 'Automatic',
    'project.mode.manual': 'Manual (step by step)',
    'project.public': 'Publish to gallery',
    'project.create': 'Create project',
    'project.no_credits': 'You have no credits. Please purchase some to create a project.',
    'project.error_no_audio': 'Please select an audio file',
    'project.error_no_title': 'Please enter a title',
    'project.start': 'Start generation',
    'project.cancel': 'Cancel',
    'project.delete': 'Delete',
    'project.download_assets': 'Download assets',
    'project.download_assets_desc': 'Script, images and videos (ZIP)',
    'project.download_assets_success': 'Archive created successfully',
    'project.download_assets_error': 'Error creating archive',
    'project.director_required': 'Director subscription required',
    'project.director_required_desc': 'Subscribe to Director to download all project files',
    'project.active_warning.title': 'Project in progress',
    'project.active_warning.message': 'You already have a project being generated: "{title}". You cannot start a new project until this one is completed.',
    'project.active_warning.view_project': 'View current project',
    
    // Script Editor
    'script.title': 'Script Editor',
    'script.scenes': 'scenes',
    'script.total_duration': 'total duration',
    'script.add_scene': 'Add scene',
    'script.validate_and_generate': 'Validate and generate images',
    'script.description': 'Description',
    'script.image_prompt': 'Image prompt',
    'script.video_prompt': 'Video prompt',
    'script.duration_s': 'Duration (seconds)',
    'script.edit_scene': 'Edit scene',
    'script.description_placeholder': 'Narrative description of the scene...',
    'script.image_prompt_placeholder': 'Detailed prompt for image generation (in English)...',
    'script.video_prompt_placeholder': 'Detailed prompt for video animation (in English)...',
    'script.scene_updated': 'Scene updated',
    'script.scene_added': 'Scene added',
    'script.scene_deleted': 'Scene deleted',
    'script.error_update': 'Error updating scene',
    'script.error_create': 'Error creating scene',
    'script.fill_all_fields': 'Please fill in all required fields',
    'script.confirm_delete_title': 'Delete scene',
    'script.confirm_delete_message': 'Are you sure you want to delete this scene?',
    'script.confirm_validate_title': 'Validate script',
    'script.confirm_validate_message': 'Validating the script will start image generation. This will consume credits. Continue?',
    'script.validate': 'Validate',
    'script.add': 'Add',
    'script.no_scenes': 'No scenes in script',
    'script.awaiting_validation': 'The script is ready. Edit it if needed then validate to start image generation.',
    
    // Image Validation
    'images.title': 'Image Validation',
    'images.images': 'images',
    'images.regenerations_left': 'regenerations left',
    'images.instructions': 'Click on an image to edit, regenerate or replace it. Then validate to start video generation.',
    'images.click_to_edit': 'Click to edit',
    'images.edit_image': 'Edit Image',
    'images.no_image': 'No image',
    'images.regenerate': 'Regenerate',
    'images.remaining': 'remaining',
    'images.regeneration_limit_reached': 'Regeneration limit reached (maximum 3)',
    'images.prompt_required': 'Image prompt (in English) is required',
    'images.upload_replacement': 'Upload image',
    'images.upload_hint': 'No limit on uploads',
    'images.confirm_regenerate_title': 'Regenerate Image',
    'images.confirm_regenerate_message': 'Do you want to regenerate this image? You have {remaining} regeneration(s) left.',
    'images.regeneration_started': 'Regeneration started',
    'images.invalid_file_type': 'Invalid file type. Please upload an image.',
    'images.file_too_large': 'File too large (max 10 MB)',
    'images.upload_success': 'Image uploaded successfully',
    'images.upload_error': 'Upload error',
    'images.confirm_validate_title': 'Validate Images',
    'images.confirm_validate_message': 'Validating images will start video generation. This will consume credits. Continue?',
    'images.validate_and_generate': 'Validate and generate videos',
    'images.awaiting_validation': 'Images are ready. Review and edit them if needed, then validate to start video generation.',
    
    // Video Validation
    'videos.title': 'Video Validation',
    'videos.videos': 'videos',
    'videos.regenerations_left': 'regenerations left',
    'videos.instructions': 'Click on a video to edit or regenerate it. Use the arrows to reorder scenes. Then validate to start the final merge.',
    'videos.click_to_edit': 'Click to edit',
    'videos.edit_video': 'Edit Video',
    'videos.no_video': 'No video',
    'videos.regenerate': 'Regenerate',
    'videos.remaining': 'remaining',
    'videos.regeneration_limit_reached': 'Regeneration limit reached (maximum 3)',
    'videos.confirm_regenerate_title': 'Regenerate Video',
    'videos.confirm_regenerate_message': 'Do you want to regenerate this video? You have {remaining} regeneration(s) left.',
    'videos.regeneration_started': 'Regeneration started',
    'videos.scene_number': 'Scene number',
    'videos.duration': 'Duration',
    'videos.move_left': 'Move left',
    'videos.move_right': 'Move right',
    'videos.scenes_swapped': 'Scenes swapped',
    'videos.confirm_validate_title': 'Validate Videos',
    'videos.confirm_validate_message': 'Validating videos will start the final merge and audio addition. Continue?',
    'videos.validate_and_merge': 'Validate and merge',
    'videos.awaiting_validation': 'Videos are ready. Reorder if needed, then validate to start the final merge with audio.',
    
    // Status (Project)
    'status.draft': 'Draft',
    'status.pending': 'Pending',
    'status.processing': 'Processing',
    'status.script_ready': 'Script ready',
    'status.images_ready': 'Images ready',
    'status.videos_ready': 'Videos ready',
    'status.completed': 'Completed',
    'status.failed': 'Failed',
    'status.cancelled': 'Cancelled',
    // Status (Scene)
    'status.image_generating': 'Generating image...',
    'status.image_ready': 'Image ready',
    'status.video_generating': 'Generating video...',
    'status.video_ready': 'Video ready',
    
    // Wizard
    'wizard.step1': 'Script',
    'wizard.step2': 'Images',
    'wizard.step3': 'Videos',
    'wizard.step4': 'Merge',
    'wizard.step5': 'Audio',
    'wizard.validate': 'Validate and continue',
    'wizard.regenerate': 'Regenerate',
    'wizard.edit': 'Edit',
    
    // Auth
    'auth.login_with_google': 'Continue with Google',
    'auth.login_with_facebook': 'Continue with Facebook',
    'auth.or': 'or',
    'auth.dev_login': 'Developer login',
    
    // Profile
    'profile.title': 'My profile',
    'profile.display_name': 'Display name',
    'profile.language': 'Language',
    'profile.theme': 'Theme',
    'profile.theme.dark': 'Dark',
    'profile.theme.light': 'Light',
    'profile.save': 'Save',
    'profile.saved': 'Saved',
    'profile.transactions': 'Transaction history',
    'profile.no_transactions': 'No transactions',
    'profile.subscription.title': 'Director Subscription',
    'profile.subscription.active': 'Active subscription',
    'profile.subscription.canceling': 'Cancellation scheduled',
    'profile.subscription.renews_on': 'Renews on {date}',
    'profile.subscription.ends_on': 'Ends on {date}',
    'profile.subscription.cancel': 'Cancel subscription',
    'profile.subscription.cancel_title': 'Cancel subscription?',
    'profile.subscription.cancel_confirm': 'Are you sure you want to cancel your Director subscription? You will keep access until the end of the current period.',
    'profile.subscription.confirm_cancel': 'Yes, cancel',
    'profile.subscription.cancelled': 'Subscription cancelled',
    'profile.subscription.cancel_error': 'Error cancelling subscription',
    'profile.subscription.reactivate': 'Reactivate subscription',
    'profile.subscription.reactivated': 'Subscription reactivated',
    'profile.subscription.reactivate_error': 'Error reactivating subscription',
    'profile.subscription.manage_payment': 'Manage payment',
    'profile.subscription.portal_error': 'Error accessing portal',
    'profile.subscription.not_subscribed': 'You are not yet subscribed to Director',
    'profile.subscription.subscribe_now': 'Subscribe now',
    'profile.subscription_success': 'Director subscription activated successfully!',
    'profile.billing.title': 'Billing',
    'profile.billing.description': 'Access your invoices, card receipts and manage your payment methods.',
    'profile.billing.access_portal': 'My Stripe Account',
    'profile.billing.portal_error': 'Unable to access billing portal',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'An error occurred',
    'common.retry': 'Retry',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.confirm': 'Confirm',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.download': 'Download',
    'common.share': 'Share',
    'common.close': 'Close',
    'common.edit': 'Edit',
    
    // Project View
    'project.not_found': 'Project not found',
    'project.back_to_dashboard': 'Back to dashboard',
    'project.processing': 'Processing... {progress}%',
    'project.your_video': 'Your video',
    'project.scenes_count': 'Scenes ({count})',
    'project.scene_number': 'Scene {number}',
    'project.confirm_delete_title': 'Confirm deletion',
    'project.confirm_delete_message': 'Are you sure you want to delete this project? This action cannot be undone.',
    // Project Create
    'project.create_subtitle': 'Create your music video in a few clicks',
    'project.drop_audio': 'Drop your audio file here',
    'project.audio_formats': 'MP3, WAV, OGG...',
    'project.section_info': 'Information',
    'project.section_mode': 'Creation mode',
    'project.section_files': 'Optional files',
    'project.mode.auto_desc': 'AI generates everything automatically',
    'project.mode.manual_desc': 'Control each step of the creation',
    'project.add_subtitles': 'Add subtitles',
    'project.add_photo': 'Add character photo',
    'project.add_intro': 'Add intro video',
    'project.add_outro': 'Add outro video',
    'project.available_credits': 'Available credits',
    
    // Profile
    'profile.section_info': 'Information',
  },
  
  es: {
    // Navigation
    'nav.home': 'Inicio',
    'nav.gallery': 'Galería',
    'nav.pricing': 'Precios',
    'nav.dashboard': 'Panel',
    'nav.login': 'Iniciar sesión',
    'nav.logout': 'Cerrar sesión',
    'nav.profile': 'Perfil',
    
    // Footer
    'footer.tagline': 'Crea increíbles videos musicales generados por IA',
    'footer.navigation': 'Navegación',
    'footer.support': 'Soporte',
    'footer.faq': 'Preguntas frecuentes',
    'footer.legal': 'Legal',
    'footer.terms': 'Términos de servicio',
    'footer.privacy': 'Política de privacidad',
    'footer.contact': 'Contacto',
    
    // Home
    'home.hero.title': 'Transforma tu música en vídeo',
    'home.hero.subtitle': 'Crea vídeos musicales impresionantes generados por IA a partir de tus pistas de audio en minutos',
    'home.hero.cta': 'Comenzar',
    'home.hero.watch': 'Ver galería',
    'home.demo.watch': 'Ver demostración',
    'home.features.title': 'Cómo funciona',
    'home.features.upload.title': 'Sube tu audio',
    'home.features.upload.desc': 'Sube tu archivo MP3, WAV o cualquier formato de audio',
    'home.features.ai.title': 'La IA crea el guión',
    'home.features.ai.desc': 'Nuestra IA analiza tu audio y genera un escenario visual único',
    'home.features.video.title': 'Recibe tu vídeo',
    'home.features.video.desc': 'Obtén un vídeo musical profesional en HD',
    'home.why.title': '¿Por qué M2V.io?',
    'home.why.fast': 'Rápido',
    'home.why.fast.desc': 'Generación en minutos, no en horas',
    'home.why.quality': 'Calidad HD',
    'home.why.quality.desc': 'Vídeos en alta definición listos para redes sociales',
    'home.why.ai': 'IA Avanzada',
    'home.why.ai.desc': 'Los últimos modelos GPT-4, DALL-E 3 y MiniMax',
    'home.why.formats': 'Todos los formatos',
    'home.why.formats.desc': 'Horizontal, Vertical, Cuadrado para todas tus necesidades',
    'home.cta.title': '¿Listo para crear tu primer vídeo?',
    'home.cta.subtitle': 'Regístrate y comienza ahora',
    
    // Gallery
    'gallery.title': 'Galería pública',
    'gallery.subtitle': 'Descubre las creaciones de nuestra comunidad',
    'gallery.empty': 'La galería está vacía por ahora',
    'gallery.empty.desc': '¡Sé el primero en compartir tu creación!',
    'gallery.mine': 'Mis vídeos',
    'gallery.filter.all': 'Todos los idiomas',
    
    // Pricing
    'pricing.title': 'Precios simples y transparentes',
    'pricing.subtitle': 'Paga solo por lo que usas, sin suscripción',
    'pricing.credit': 'crédito',
    'pricing.credits': 'créditos',
    'pricing.per_credit': 'por crédito',
    'pricing.duration': '30 segundos de vídeo',
    'pricing.buy': 'Comprar',
    'pricing.popular': 'Popular',
    'pricing.how.title': '¿Cómo funcionan los créditos?',
    'pricing.how.desc': 'Un crédito te permite generar 30 segundos de vídeo. Los créditos no son divisibles: un vídeo de 35 segundos consumirá 2 créditos.',
    'pricing.example': 'Ejemplo: Para una canción de 3 minutos (180 segundos), necesitarás 6 créditos.',
    'pricing.included.title': 'Incluido con cada crédito',
    'pricing.included.script': 'Guión generado por IA',
    'pricing.included.images': 'Imágenes de alta calidad',
    'pricing.included.video': 'Vídeo HD animado',
    'pricing.included.audio': 'Sincronización de audio',
    'pricing.included.download': 'Descarga ilimitada',
    'pricing.included.formats': 'Todos los formatos (16:9, 9:16, 1:1)',
    'pricing.included.no_watermark': 'Sin marca de agua',
    'pricing.included.intro_video': 'Vídeo de intro personalizado',
    'pricing.select_credits': 'Elige tus créditos',
    'pricing.of_video': 'de vídeo',
    'pricing.over_slider_max': 'Valor superior a {max} créditos',
    
    // Director subscription
    'pricing.director.title': 'Director',
    'pricing.director.description': 'Accede a todos los archivos de tus proyectos',
    'pricing.director.month': 'mes',
    'pricing.director.subscribe': 'Suscribirse a Director',
    'pricing.director.active': 'Suscripción activa',
    'pricing.director.canceling': 'Suscripción cancelándose',
    'pricing.director.manage': 'Gestionar mi suscripción',
    'pricing.director.features.download_assets': 'Descargar todos los assets del proyecto',
    'pricing.director.features.script_json': 'Script completo en JSON',
    'pricing.director.features.all_images': 'Todas las imágenes generadas (PNG)',
    'pricing.director.features.all_videos': 'Todos los vídeos intermedios (MP4)',
    'pricing.director.features.zip_archive': 'Archivo ZIP completo',
    
    // Terms
    'terms.title': 'Términos de servicio',
    'terms.last_update': 'Última actualización',
    'terms.acceptance.title': 'Aceptación de los términos',
    'terms.acceptance.content': 'Al acceder y utilizar M2V.io, aceptas estar sujeto a estos términos de servicio. Si no aceptas estos términos, por favor no utilices nuestro servicio.',
    'terms.service.title': 'Descripción del servicio',
    'terms.service.content': 'M2V.io es una plataforma de generación de vídeos musicales asistida por inteligencia artificial. Nuestro servicio permite a los usuarios:',
    'terms.service.item1': 'Subir archivos de audio',
    'terms.service.item2': 'Generar guiones visuales mediante IA',
    'terms.service.item3': 'Crear vídeos animados a partir de imágenes generadas',
    'terms.service.item4': 'Descargar y compartir los vídeos creados',
    'terms.credits.title': 'Sistema de créditos',
    'terms.credits.content': 'El uso del servicio requiere créditos. Se aplican las siguientes condiciones:',
    'terms.credits.item1': 'Un crédito permite generar 30 segundos de vídeo',
    'terms.credits.item2': 'Los créditos no son divisibles ni reembolsables',
    'terms.credits.item3': 'Los créditos no caducan y permanecen vinculados a tu cuenta',
    'terms.content.title': 'Contenido del usuario',
    'terms.content.content': 'Eres responsable del contenido que subes. Está prohibido subir:',
    'terms.content.item1': 'Contenido protegido por derechos de autor sin autorización',
    'terms.content.item2': 'Contenido ilegal, difamatorio u ofensivo',
    'terms.content.item3': 'Contenido que contenga virus o malware',
    'terms.intellectual.title': 'Propiedad intelectual',
    'terms.intellectual.content': 'Conservas todos los derechos sobre el contenido de audio que subes. Los vídeos generados te pertenecen y puedes utilizarlos libremente con fines personales o comerciales. M2V.io se reserva el derecho de utilizar los vídeos publicados en la galería pública con fines promocionales.',
    'terms.liability.title': 'Limitación de responsabilidad',
    'terms.liability.content': 'M2V.io se proporciona "tal cual" sin garantía de ningún tipo. No somos responsables de los daños directos o indirectos derivados del uso de nuestro servicio. Los resultados de la generación IA pueden variar y no están garantizados.',
    'terms.termination.title': 'Terminación',
    'terms.termination.content': 'Nos reservamos el derecho de suspender o terminar tu cuenta en caso de violación de estos términos. En caso de terminación, perderás el acceso a tu cuenta pero conservarás los vídeos ya descargados.',
    'terms.modifications.title': 'Modificaciones',
    'terms.modifications.content': 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones serán efectivas tras su publicación en esta página. Tu uso continuado del servicio después de la modificación constituye la aceptación de los nuevos términos.',
    'terms.contact.title': 'Contacto',
    'terms.contact.content': 'Para cualquier pregunta sobre estos términos de servicio, contáctanos en:',
    
    // Privacy
    'privacy.title': 'Política de privacidad',
    'privacy.last_update': 'Última actualización',
    'privacy.intro.title': 'Introducción',
    'privacy.intro.content': 'M2V.io se compromete a proteger tu privacidad. Esta política explica cómo recopilamos, utilizamos y protegemos tus datos personales de acuerdo con el RGPD.',
    'privacy.collected.title': 'Datos recopilados',
    'privacy.collected.content': 'Recopilamos los siguientes datos:',
    'privacy.collected.item1': 'Información de cuenta: email, nombre para mostrar, foto de perfil (vía Google/Facebook)',
    'privacy.collected.item2': 'Datos de uso: proyectos creados, archivos subidos, preferencias',
    'privacy.collected.item3': 'Datos de pago: procesados por Stripe, no almacenamos tu información de tarjeta',
    'privacy.collected.item4': 'Datos técnicos: dirección IP, tipo de navegador, registros de conexión',
    'privacy.usage.title': 'Uso de datos',
    'privacy.usage.content': 'Tus datos se utilizan para:',
    'privacy.usage.item1': 'Proporcionar y mejorar nuestros servicios',
    'privacy.usage.item2': 'Procesar tus pagos y gestionar tu cuenta',
    'privacy.usage.item3': 'Enviarte comunicaciones importantes (sin spam)',
    'privacy.usage.item4': 'Analizar el uso para mejorar el servicio',
    'privacy.storage.title': 'Conservación de datos',
    'privacy.storage.content': 'Tus datos personales se conservan mientras tu cuenta esté activa. Los archivos de audio y vídeo se almacenan durante 12 meses después de su creación. Puedes solicitar la eliminación de tus datos en cualquier momento.',
    'privacy.sharing.title': 'Compartición de datos',
    'privacy.sharing.content': 'Nunca vendemos tus datos. Solo los compartimos con:',
    'privacy.sharing.item1': 'Nuestros proveedores técnicos (alojamiento, pago) bajo acuerdo de confidencialidad',
    'privacy.sharing.item2': 'Las autoridades si lo exige la ley',
    'privacy.cookies.title': 'Cookies',
    'privacy.cookies.content': 'Utilizamos cookies esenciales para el funcionamiento del sitio (autenticación, preferencias). No utilizamos cookies de seguimiento publicitario.',
    'privacy.rights.title': 'Tus derechos',
    'privacy.rights.content': 'Según el RGPD, tienes los siguientes derechos:',
    'privacy.rights.item1': 'Derecho de acceso: obtener una copia de tus datos',
    'privacy.rights.item2': 'Derecho de rectificación: corregir tus datos',
    'privacy.rights.item3': 'Derecho de supresión: eliminar tus datos',
    'privacy.rights.item4': 'Derecho de portabilidad: exportar tus datos',
    'privacy.security.title': 'Seguridad',
    'privacy.security.content': 'Implementamos medidas de seguridad apropiadas para proteger tus datos: cifrado HTTPS, autenticación segura, acceso restringido a los datos.',
    'privacy.contact.title': 'Contacto DPO',
    'privacy.contact.content': 'Para ejercer tus derechos o para cualquier pregunta relativa a la protección de tus datos, contacta a nuestro delegado de protección de datos:',
    
    // Contact
    'contact.title': 'Contáctanos',
    'contact.subtitle': '¿Una pregunta, una sugerencia? Estamos aquí para ayudarte.',
    'contact.response_time': 'Tiempo de respuesta',
    'contact.response_delay': 'En 24-48 horas laborables',
    'contact.languages': 'Idiomas soportados',
    'contact.form.name': 'Nombre',
    'contact.form.name_placeholder': 'Tu nombre',
    'contact.form.email': 'Email',
    'contact.form.email_placeholder': 'tu@email.com',
    'contact.form.subject': 'Asunto',
    'contact.form.subject_placeholder': 'Selecciona un asunto',
    'contact.form.message': 'Mensaje',
    'contact.form.message_placeholder': 'Describe tu solicitud en detalle...',
    'contact.form.captcha': 'Verificación anti-spam',
    'contact.form.captcha_hint': 'Resuelve este cálculo simple para demostrar que eres humano.',
    'contact.form.captcha_placeholder': 'Respuesta',
    'contact.form.captcha_refresh': 'Nuevo cálculo',
    'contact.form.send': 'Enviar mensaje',
    'contact.form.invalid': 'Por favor completa todos los campos correctamente',
    'contact.form.success': '¡Mensaje enviado con éxito!',
    'contact.form.error': 'Error al enviar, por favor inténtalo de nuevo',
    'contact.form.sent_title': '¡Mensaje enviado!',
    'contact.form.sent_message': 'Hemos recibido tu mensaje y te responderemos lo antes posible.',
    'contact.form.new_message': 'Enviar otro mensaje',
    'contact.subject.general': 'Pregunta general',
    'contact.subject.support': 'Soporte técnico',
    'contact.subject.billing': 'Facturación',
    'contact.subject.partnership': 'Colaboración',
    'contact.subject.other': 'Otro',
    
    // FAQ
    'faq.title': 'Preguntas frecuentes',
    'faq.subtitle': 'Encuentra rápidamente las respuestas a tus preguntas más comunes',
    'faq.q1.question': '¿Cuánto tiempo se tarda en generar un vídeo de 3 minutos?',
    'faq.q1.answer': 'Se necesitan aproximadamente 40 minutos para obtener el vídeo en modo automático.',
    'faq.q2.question': '¿Cuál es la diferencia entre el modo automático y el manual?',
    'faq.q2.answer_intro': 'Ambos modos ofrecen diferentes niveles de control:',
    'faq.q2.auto_title': 'Modo automático',
    'faq.q2.auto_desc': 'Sigues el progreso de la generación sin ninguna intervención de tu parte.',
    'faq.q2.manual_title': 'Modo manual',
    'faq.q2.manual_desc': 'Puedes editar y validar el guión, ver las imágenes de cada escena y modificar hasta 3, y finalmente ver los vídeos de cada escena, cambiar el orden y regenerar hasta 3 vídeos.',
    'faq.q3.question': '¿Qué incluye la suscripción adicional "Director"?',
    'faq.q3.answer': 'Además de descargar el vídeo final, podrás descargar todos los elementos que componen el vídeo:',
    'faq.q3.item1': 'El guión completo del proyecto',
    'faq.q3.item2': 'Todas las imágenes generadas para cada escena',
    'faq.q3.item3': 'Todos los vídeos intermedios',
    'faq.q4.question': '¿Qué formato de vídeo puedo producir?',
    'faq.q4.answer': 'Puedes crear vídeos en formato 16:9 (horizontal), 9:16 (vertical) o 1:1 (cuadrado). El archivo de salida está en formato MP4.',
    'faq.q5.question': '¿Es posible cancelar un proyecto?',
    'faq.q5.answer': 'Sí, pero dependiendo de la etapa en el momento de la cancelación, se cobrarán cargos: 1 crédito si cancelas antes de la generación de vídeos, y si ya se han generado vídeos, es la duración de los vídeos generados lo que determinará el número de créditos consumidos.',
    'faq.q6.question': '¿Se puede recuperar un proyecto si lo he eliminado?',
    'faq.q6.answer': 'No, los proyectos eliminados no se pueden recuperar. Asegúrate de descargar todos los archivos deseados antes de eliminar un proyecto.',
    'faq.more_questions': '¿Tienes más preguntas?',
    'faq.contact_us': 'Nuestro equipo está aquí para ayudarte.',
    'faq.contact_button': 'Contáctanos',
    
    // Dashboard
    'dashboard.title': 'Panel de control',
    'dashboard.welcome': 'Bienvenido',
    'dashboard.credits': 'Créditos disponibles',
    'dashboard.projects': 'Mis proyectos',
    'dashboard.new_project': 'Nuevo proyecto',
    'dashboard.payment_success': '¡Pago exitoso! Tus créditos han sido añadidos.',
    'dashboard.no_projects': 'Sin proyectos',
    'dashboard.create_first': '¡Crea tu primer vídeo musical!',
    
    // Project
    'project.title': 'Título del proyecto',
    'project.language': 'Idioma',
    'project.format': 'Formato de vídeo',
    'project.format.landscape': 'Horizontal (16:9)',
    'project.format.portrait': 'Vertical (9:16)',
    'project.format.square': 'Cuadrado (1:1)',
    'project.style': 'Estilo visual',
    'project.mood': 'Ambiente / Descripción',
    'project.audio': 'Archivo de audio',
    'project.srt': 'Subtítulos (opcional)',
    'project.intro': 'Intro de vídeo (opcional)',
    'project.outro': 'Outro de vídeo (opcional)',
    'project.photo': 'Foto del personaje (opcional)',
    'project.mode': 'Modo de creación',
    'project.mode.auto': 'Automático',
    'project.mode.manual': 'Manual (paso a paso)',
    'project.public': 'Publicar en la galería',
    'project.create': 'Crear proyecto',
    'project.no_credits': 'No tienes créditos. Por favor compra algunos para crear un proyecto.',
    'project.error_no_audio': 'Por favor selecciona un archivo de audio',
    'project.error_no_title': 'Por favor ingresa un título',
    'project.start': 'Iniciar generación',
    'project.cancel': 'Cancelar',
    'project.delete': 'Eliminar',
    'project.download_assets': 'Descargar assets',
    'project.download_assets_desc': 'Guión, imágenes y vídeos (ZIP)',
    'project.download_assets_success': 'Archivo creado con éxito',
    'project.download_assets_error': 'Error al crear el archivo',
    'project.director_required': 'Se requiere suscripción Director',
    'project.director_required_desc': 'Suscríbete a Director para descargar todos los archivos del proyecto',
    'project.active_warning.title': 'Proyecto en curso',
    'project.active_warning.message': 'Ya tienes un proyecto en generación: "{title}". No puedes iniciar un nuevo proyecto hasta que este se complete.',
    'project.active_warning.view_project': 'Ver proyecto actual',
    
    // Script Editor
    'script.title': 'Editor de guión',
    'script.scenes': 'escenas',
    'script.total_duration': 'duración total',
    'script.add_scene': 'Añadir escena',
    'script.validate_and_generate': 'Validar y generar imágenes',
    'script.description': 'Descripción',
    'script.image_prompt': 'Prompt de imagen',
    'script.video_prompt': 'Prompt de vídeo',
    'script.duration_s': 'Duración (segundos)',
    'script.edit_scene': 'Editar escena',
    'script.description_placeholder': 'Descripción narrativa de la escena...',
    'script.image_prompt_placeholder': 'Prompt detallado para generación de imagen (en inglés)...',
    'script.video_prompt_placeholder': 'Prompt detallado para animación de vídeo (en inglés)...',
    'script.scene_updated': 'Escena actualizada',
    'script.scene_added': 'Escena añadida',
    'script.scene_deleted': 'Escena eliminada',
    'script.error_update': 'Error al actualizar',
    'script.error_create': 'Error al crear',
    'script.fill_all_fields': 'Por favor complete todos los campos obligatorios',
    'script.confirm_delete_title': 'Eliminar escena',
    'script.confirm_delete_message': '¿Está seguro de que desea eliminar esta escena?',
    'script.confirm_validate_title': 'Validar guión',
    'script.confirm_validate_message': 'Validar el guión iniciará la generación de imágenes. Esto consumirá créditos. ¿Continuar?',
    'script.validate': 'Validar',
    'script.add': 'Añadir',
    'script.no_scenes': 'No hay escenas en el guión',
    'script.awaiting_validation': 'El guión está listo. Modifíquelo si es necesario y luego valide para iniciar la generación de imágenes.',
    
    // Image Validation
    'images.title': 'Validación de imágenes',
    'images.images': 'imágenes',
    'images.regenerations_left': 'regeneraciones restantes',
    'images.instructions': 'Haga clic en una imagen para editarla, regenerarla o reemplazarla. Luego valide para iniciar la generación de vídeos.',
    'images.click_to_edit': 'Clic para editar',
    'images.edit_image': 'Editar imagen',
    'images.no_image': 'Sin imagen',
    'images.regenerate': 'Regenerar',
    'images.remaining': 'restantes',
    'images.regeneration_limit_reached': 'Límite de regeneración alcanzado (máximo 3)',
    'images.prompt_required': 'El prompt de imagen (en inglés) es requerido',
    'images.upload_replacement': 'Subir imagen',
    'images.upload_hint': 'Sin límite en las subidas',
    'images.confirm_regenerate_title': 'Regenerar imagen',
    'images.confirm_regenerate_message': '¿Desea regenerar esta imagen? Le quedan {remaining} regeneración(es).',
    'images.regeneration_started': 'Regeneración iniciada',
    'images.invalid_file_type': 'Tipo de archivo no válido. Por favor suba una imagen.',
    'images.file_too_large': 'Archivo demasiado grande (máx 10 MB)',
    'images.upload_success': 'Imagen subida correctamente',
    'images.upload_error': 'Error al subir',
    'images.confirm_validate_title': 'Validar imágenes',
    'images.confirm_validate_message': 'Validar las imágenes iniciará la generación de vídeos. Esto consumirá créditos. ¿Continuar?',
    'images.validate_and_generate': 'Validar y generar vídeos',
    'images.awaiting_validation': 'Las imágenes están listas. Revíselas y edítelas si es necesario, luego valide para iniciar la generación de vídeos.',
    
    // Video Validation
    'videos.title': 'Validación de vídeos',
    'videos.videos': 'vídeos',
    'videos.regenerations_left': 'regeneraciones restantes',
    'videos.instructions': 'Haga clic en un vídeo para editarlo o regenerarlo. Use las flechas para reordenar las escenas. Luego valide para iniciar la fusión final.',
    'videos.click_to_edit': 'Clic para editar',
    'videos.edit_video': 'Editar vídeo',
    'videos.no_video': 'Sin vídeo',
    'videos.regenerate': 'Regenerar',
    'videos.remaining': 'restantes',
    'videos.regeneration_limit_reached': 'Límite de regeneración alcanzado (máximo 3)',
    'videos.confirm_regenerate_title': 'Regenerar vídeo',
    'videos.confirm_regenerate_message': '¿Desea regenerar este vídeo? Le quedan {remaining} regeneración(es).',
    'videos.regeneration_started': 'Regeneración iniciada',
    'videos.scene_number': 'Número de escena',
    'videos.duration': 'Duración',
    'videos.move_left': 'Mover a la izquierda',
    'videos.move_right': 'Mover a la derecha',
    'videos.scenes_swapped': 'Escenas intercambiadas',
    'videos.confirm_validate_title': 'Validar vídeos',
    'videos.confirm_validate_message': 'Validar los vídeos iniciará la fusión final y la adición de audio. ¿Continuar?',
    'videos.validate_and_merge': 'Validar y fusionar',
    'videos.awaiting_validation': 'Los vídeos están listos. Reordene si es necesario, luego valide para iniciar la fusión final con audio.',
    
    // Status (Project)
    'status.draft': 'Borrador',
    'status.pending': 'Pendiente',
    'status.processing': 'Procesando',
    'status.script_ready': 'Guión listo',
    'status.images_ready': 'Imágenes listas',
    'status.videos_ready': 'Vídeos listos',
    'status.completed': 'Completado',
    'status.failed': 'Fallido',
    'status.cancelled': 'Cancelado',
    // Status (Scene)
    'status.image_generating': 'Generando imagen...',
    'status.image_ready': 'Imagen lista',
    'status.video_generating': 'Generando vídeo...',
    'status.video_ready': 'Vídeo listo',
    
    // Wizard
    'wizard.step1': 'Guión',
    'wizard.step2': 'Imágenes',
    'wizard.step3': 'Vídeos',
    'wizard.step4': 'Fusión',
    'wizard.step5': 'Audio',
    'wizard.validate': 'Validar y continuar',
    'wizard.regenerate': 'Regenerar',
    'wizard.edit': 'Editar',
    
    // Auth
    'auth.login_with_google': 'Continuar con Google',
    'auth.login_with_facebook': 'Continuar con Facebook',
    'auth.or': 'o',
    'auth.dev_login': 'Inicio de sesión de desarrollador',
    
    // Profile
    'profile.title': 'Mi perfil',
    'profile.display_name': 'Nombre para mostrar',
    'profile.language': 'Idioma',
    'profile.theme': 'Tema',
    'profile.theme.dark': 'Oscuro',
    'profile.theme.light': 'Claro',
    'profile.save': 'Guardar',
    'profile.saved': 'Guardado',
    'profile.transactions': 'Historial de transacciones',
    'profile.no_transactions': 'Sin transacciones',
    'profile.subscription.title': 'Suscripción Director',
    'profile.subscription.active': 'Suscripción activa',
    'profile.subscription.canceling': 'Cancelación programada',
    'profile.subscription.renews_on': 'Se renueva el {date}',
    'profile.subscription.ends_on': 'Termina el {date}',
    'profile.subscription.cancel': 'Cancelar suscripción',
    'profile.subscription.cancel_title': '¿Cancelar suscripción?',
    'profile.subscription.cancel_confirm': '¿Estás seguro de que quieres cancelar tu suscripción Director? Mantendrás el acceso hasta el final del período actual.',
    'profile.subscription.confirm_cancel': 'Sí, cancelar',
    'profile.subscription.cancelled': 'Suscripción cancelada',
    'profile.subscription.cancel_error': 'Error al cancelar la suscripción',
    'profile.subscription.reactivate': 'Reactivar suscripción',
    'profile.subscription.reactivated': 'Suscripción reactivada',
    'profile.subscription.reactivate_error': 'Error al reactivar la suscripción',
    'profile.subscription.manage_payment': 'Gestionar pago',
    'profile.subscription.portal_error': 'Error al acceder al portal',
    'profile.subscription.not_subscribed': 'Aún no estás suscrito a Director',
    'profile.subscription.subscribe_now': 'Suscribirse ahora',
    'profile.subscription_success': '¡Suscripción Director activada con éxito!',
    'profile.billing.title': 'Facturación',
    'profile.billing.description': 'Accede a tus facturas, recibos de tarjeta y gestiona tus métodos de pago.',
    'profile.billing.access_portal': 'Mi cuenta Stripe',
    'profile.billing.portal_error': 'No se puede acceder al portal de facturación',
    
    // Common
    'common.loading': 'Cargando...',
    'common.error': 'Ocurrió un error',
    'common.retry': 'Reintentar',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.confirm': 'Confirmar',
    'common.back': 'Volver',
    'common.next': 'Siguiente',
    'common.download': 'Descargar',
    'common.share': 'Compartir',
    'common.close': 'Cerrar',
    'common.edit': 'Editar',
    
    // Project View
    'project.not_found': 'Proyecto no encontrado',
    'project.back_to_dashboard': 'Volver al panel',
    'project.processing': 'Procesando... {progress}%',
    'project.your_video': 'Tu video',
    'project.scenes_count': 'Escenas ({count})',
    'project.scene_number': 'Escena {number}',
    'project.confirm_delete_title': 'Confirmar eliminación',
    'project.confirm_delete_message': '¿Estás seguro de que quieres eliminar este proyecto? Esta acción es irreversible.',
    // Project Create
    'project.create_subtitle': 'Crea tu video musical en unos clics',
    'project.drop_audio': 'Arrastra tu archivo de audio aquí',
    'project.audio_formats': 'MP3, WAV, OGG...',
    'project.section_info': 'Información',
    'project.section_mode': 'Modo de creación',
    'project.section_files': 'Archivos opcionales',
    'project.mode.auto_desc': 'La IA genera todo automáticamente',
    'project.mode.manual_desc': 'Controla cada paso de la creación',
    'project.add_subtitles': 'Añadir subtítulos',
    'project.add_photo': 'Añadir foto de personaje',
    'project.add_intro': 'Añadir video de intro',
    'project.add_outro': 'Añadir video de outro',
    'project.available_credits': 'Créditos disponibles',
    
    // Profile
    'profile.section_info': 'Información',
  },
  
  it: {
    // Navigation
    'nav.home': 'Home',
    'nav.gallery': 'Galleria',
    'nav.pricing': 'Prezzi',
    'nav.dashboard': 'Dashboard',
    'nav.login': 'Accedi',
    'nav.logout': 'Esci',
    'nav.profile': 'Profilo',
    
    // Footer
    'footer.tagline': 'Crea straordinari video musicali generati dall\'IA',
    'footer.navigation': 'Navigazione',
    'footer.support': 'Supporto',
    'footer.faq': 'FAQ',
    'footer.legal': 'Legale',
    'footer.terms': 'Termini di servizio',
    'footer.privacy': 'Privacy',
    'footer.contact': 'Contatto',
    
    // Home
    'home.hero.title': 'Trasforma la tua musica in video',
    'home.hero.subtitle': 'Crea video musicali straordinari generati dall\'IA dalle tue tracce audio in pochi minuti',
    'home.hero.cta': 'Inizia',
    'home.hero.watch': 'Vedi galleria',
    'home.demo.watch': 'Guarda demo',
    'home.features.title': 'Come funziona',
    'home.features.upload.title': 'Carica il tuo audio',
    'home.features.upload.desc': 'Carica il tuo file MP3, WAV o qualsiasi formato audio',
    'home.features.ai.title': 'L\'IA crea lo script',
    'home.features.ai.desc': 'La nostra IA analizza il tuo audio e genera uno scenario visivo unico',
    'home.features.video.title': 'Ricevi il tuo video',
    'home.features.video.desc': 'Ottieni un video musicale professionale in HD',
    'home.why.title': 'Perché M2V.io?',
    'home.why.fast': 'Veloce',
    'home.why.fast.desc': 'Generazione in minuti, non ore',
    'home.why.quality': 'Qualità HD',
    'home.why.quality.desc': 'Video in alta definizione pronti per i social',
    'home.why.ai': 'IA Avanzata',
    'home.why.ai.desc': 'Gli ultimi modelli GPT-4, DALL-E 3 e MiniMax',
    'home.why.formats': 'Tutti i formati',
    'home.why.formats.desc': 'Orizzontale, Verticale, Quadrato per tutte le tue esigenze',
    'home.cta.title': 'Pronto a creare il tuo primo video?',
    'home.cta.subtitle': 'Registrati e inizia ora',
    
    // Gallery
    'gallery.title': 'Galleria pubblica',
    'gallery.subtitle': 'Scopri le creazioni della nostra community',
    'gallery.empty': 'La galleria è vuota al momento',
    'gallery.empty.desc': 'Sii il primo a condividere la tua creazione!',
    'gallery.mine': 'I miei video',
    'gallery.filter.all': 'Tutte le lingue',
    
    // Pricing
    'pricing.title': 'Prezzi semplici e trasparenti',
    'pricing.subtitle': 'Paga solo per quello che usi, senza abbonamento',
    'pricing.credit': 'credito',
    'pricing.credits': 'crediti',
    'pricing.per_credit': 'per credito',
    'pricing.duration': '30 secondi di video',
    'pricing.buy': 'Acquista',
    'pricing.popular': 'Popolare',
    'pricing.how.title': 'Come funzionano i crediti?',
    'pricing.how.desc': 'Un credito ti permette di generare 30 secondi di video. I crediti non sono divisibili: un video di 35 secondi consumerà 2 crediti.',
    'pricing.example': 'Esempio: Per una canzone di 3 minuti (180 secondi), avrai bisogno di 6 crediti.',
    'pricing.included.title': 'Incluso con ogni credito',
    'pricing.included.script': 'Script generato dall\'IA',
    'pricing.included.images': 'Immagini di alta qualità',
    'pricing.included.video': 'Video HD animato',
    'pricing.included.audio': 'Sincronizzazione audio',
    'pricing.included.download': 'Download illimitato',
    'pricing.included.formats': 'Tutti i formati (16:9, 9:16, 1:1)',
    'pricing.included.no_watermark': 'Nessun watermark',
    'pricing.included.intro_video': 'Video intro personalizzato',
    'pricing.select_credits': 'Scegli i tuoi crediti',
    'pricing.of_video': 'di video',
    'pricing.over_slider_max': 'Valore superiore a {max} crediti',
    
    // Director subscription
    'pricing.director.title': 'Director',
    'pricing.director.description': 'Accedi a tutti i file dei tuoi progetti',
    'pricing.director.month': 'mese',
    'pricing.director.subscribe': 'Abbonati a Director',
    'pricing.director.active': 'Abbonamento attivo',
    'pricing.director.canceling': 'Abbonamento in cancellazione',
    'pricing.director.manage': 'Gestisci il mio abbonamento',
    'pricing.director.features.download_assets': 'Scarica tutti gli asset del progetto',
    'pricing.director.features.script_json': 'Script completo in JSON',
    'pricing.director.features.all_images': 'Tutte le immagini generate (PNG)',
    'pricing.director.features.all_videos': 'Tutti i video intermedi (MP4)',
    'pricing.director.features.zip_archive': 'Archivio ZIP completo',
    
    // Terms
    'terms.title': 'Termini di servizio',
    'terms.last_update': 'Ultimo aggiornamento',
    'terms.acceptance.title': 'Accettazione dei termini',
    'terms.acceptance.content': 'Accedendo e utilizzando M2V.io, accetti di essere vincolato da questi termini di servizio. Se non accetti questi termini, ti preghiamo di non utilizzare il nostro servizio.',
    'terms.service.title': 'Descrizione del servizio',
    'terms.service.content': 'M2V.io è una piattaforma di generazione di video musicali assistita dall\'intelligenza artificiale. Il nostro servizio consente agli utenti di:',
    'terms.service.item1': 'Caricare file audio',
    'terms.service.item2': 'Generare script visivi tramite IA',
    'terms.service.item3': 'Creare video animati da immagini generate',
    'terms.service.item4': 'Scaricare e condividere i video creati',
    'terms.credits.title': 'Sistema di crediti',
    'terms.credits.content': 'L\'utilizzo del servizio richiede crediti. Si applicano le seguenti condizioni:',
    'terms.credits.item1': 'Un credito permette di generare 30 secondi di video',
    'terms.credits.item2': 'I crediti non sono divisibili e non sono rimborsabili',
    'terms.credits.item3': 'I crediti non scadono e rimangono collegati al tuo account',
    'terms.content.title': 'Contenuto dell\'utente',
    'terms.content.content': 'Sei responsabile dei contenuti che carichi. È vietato caricare:',
    'terms.content.item1': 'Contenuti protetti da copyright senza autorizzazione',
    'terms.content.item2': 'Contenuti illegali, diffamatori o offensivi',
    'terms.content.item3': 'Contenuti contenenti virus o malware',
    'terms.intellectual.title': 'Proprietà intellettuale',
    'terms.intellectual.content': 'Mantieni tutti i diritti sul contenuto audio che carichi. I video generati ti appartengono e puoi utilizzarli liberamente per scopi personali o commerciali. M2V.io si riserva il diritto di utilizzare i video pubblicati nella galleria pubblica per scopi promozionali.',
    'terms.liability.title': 'Limitazione di responsabilità',
    'terms.liability.content': 'M2V.io viene fornito "così com\'è" senza garanzia di alcun tipo. Non siamo responsabili per danni diretti o indiretti derivanti dall\'uso del nostro servizio. I risultati della generazione IA possono variare e non sono garantiti.',
    'terms.termination.title': 'Terminazione',
    'terms.termination.content': 'Ci riserviamo il diritto di sospendere o terminare il tuo account in caso di violazione di questi termini. In caso di terminazione, perderai l\'accesso al tuo account ma manterrai i video già scaricati.',
    'terms.modifications.title': 'Modifiche',
    'terms.modifications.content': 'Ci riserviamo il diritto di modificare questi termini in qualsiasi momento. Le modifiche saranno effettive dopo la pubblicazione su questa pagina. Il tuo uso continuato del servizio dopo la modifica costituisce l\'accettazione dei nuovi termini.',
    'terms.contact.title': 'Contatto',
    'terms.contact.content': 'Per qualsiasi domanda riguardante questi termini di servizio, contattaci a:',
    
    // Privacy
    'privacy.title': 'Informativa sulla privacy',
    'privacy.last_update': 'Ultimo aggiornamento',
    'privacy.intro.title': 'Introduzione',
    'privacy.intro.content': 'M2V.io si impegna a proteggere la tua privacy. Questa politica spiega come raccogliamo, utilizziamo e proteggiamo i tuoi dati personali in conformità con il GDPR.',
    'privacy.collected.title': 'Dati raccolti',
    'privacy.collected.content': 'Raccogliamo i seguenti dati:',
    'privacy.collected.item1': 'Informazioni dell\'account: email, nome visualizzato, foto profilo (tramite Google/Facebook)',
    'privacy.collected.item2': 'Dati di utilizzo: progetti creati, file caricati, preferenze',
    'privacy.collected.item3': 'Dati di pagamento: elaborati da Stripe, non memorizziamo le informazioni della tua carta',
    'privacy.collected.item4': 'Dati tecnici: indirizzo IP, tipo di browser, log di connessione',
    'privacy.usage.title': 'Utilizzo dei dati',
    'privacy.usage.content': 'I tuoi dati vengono utilizzati per:',
    'privacy.usage.item1': 'Fornire e migliorare i nostri servizi',
    'privacy.usage.item2': 'Elaborare i tuoi pagamenti e gestire il tuo account',
    'privacy.usage.item3': 'Inviarti comunicazioni importanti (niente spam)',
    'privacy.usage.item4': 'Analizzare l\'utilizzo per migliorare il servizio',
    'privacy.storage.title': 'Conservazione dei dati',
    'privacy.storage.content': 'I tuoi dati personali vengono conservati finché il tuo account è attivo. I file audio e video vengono memorizzati per 12 mesi dopo la creazione. Puoi richiedere la cancellazione dei tuoi dati in qualsiasi momento.',
    'privacy.sharing.title': 'Condivisione dei dati',
    'privacy.sharing.content': 'Non vendiamo mai i tuoi dati. Li condividiamo solo con:',
    'privacy.sharing.item1': 'I nostri fornitori tecnici (hosting, pagamento) sotto accordo di riservatezza',
    'privacy.sharing.item2': 'Le autorità se richiesto dalla legge',
    'privacy.cookies.title': 'Cookie',
    'privacy.cookies.content': 'Utilizziamo cookie essenziali per il funzionamento del sito (autenticazione, preferenze). Non utilizziamo cookie di tracciamento pubblicitario.',
    'privacy.rights.title': 'I tuoi diritti',
    'privacy.rights.content': 'Ai sensi del GDPR, hai i seguenti diritti:',
    'privacy.rights.item1': 'Diritto di accesso: ottenere una copia dei tuoi dati',
    'privacy.rights.item2': 'Diritto di rettifica: correggere i tuoi dati',
    'privacy.rights.item3': 'Diritto alla cancellazione: eliminare i tuoi dati',
    'privacy.rights.item4': 'Diritto alla portabilità: esportare i tuoi dati',
    'privacy.security.title': 'Sicurezza',
    'privacy.security.content': 'Implementiamo misure di sicurezza appropriate per proteggere i tuoi dati: crittografia HTTPS, autenticazione sicura, accesso ristretto ai dati.',
    'privacy.contact.title': 'Contatto DPO',
    'privacy.contact.content': 'Per esercitare i tuoi diritti o per qualsiasi domanda relativa alla protezione dei tuoi dati, contatta il nostro responsabile della protezione dei dati:',
    
    // Contact
    'contact.title': 'Contattaci',
    'contact.subtitle': 'Una domanda, un suggerimento? Siamo qui per aiutarti.',
    'contact.response_time': 'Tempo di risposta',
    'contact.response_delay': 'Entro 24-48 ore lavorative',
    'contact.languages': 'Lingue supportate',
    'contact.form.name': 'Nome',
    'contact.form.name_placeholder': 'Il tuo nome',
    'contact.form.email': 'Email',
    'contact.form.email_placeholder': 'tua@email.com',
    'contact.form.subject': 'Oggetto',
    'contact.form.subject_placeholder': 'Seleziona un oggetto',
    'contact.form.message': 'Messaggio',
    'contact.form.message_placeholder': 'Descrivi la tua richiesta in dettaglio...',
    'contact.form.captcha': 'Verifica anti-spam',
    'contact.form.captcha_hint': 'Risolvi questo semplice calcolo per dimostrare che sei umano.',
    'contact.form.captcha_placeholder': 'Risposta',
    'contact.form.captcha_refresh': 'Nuovo calcolo',
    'contact.form.send': 'Invia messaggio',
    'contact.form.invalid': 'Compila tutti i campi correttamente',
    'contact.form.success': 'Messaggio inviato con successo!',
    'contact.form.error': 'Errore durante l\'invio, riprova',
    'contact.form.sent_title': 'Messaggio inviato!',
    'contact.form.sent_message': 'Abbiamo ricevuto il tuo messaggio e ti risponderemo il prima possibile.',
    'contact.form.new_message': 'Invia un altro messaggio',
    'contact.subject.general': 'Domanda generale',
    'contact.subject.support': 'Supporto tecnico',
    'contact.subject.billing': 'Fatturazione',
    'contact.subject.partnership': 'Partnership',
    'contact.subject.other': 'Altro',
    
    // FAQ
    'faq.title': 'Domande frequenti',
    'faq.subtitle': 'Trova rapidamente le risposte alle tue domande più comuni',
    'faq.q1.question': 'Quanto tempo ci vuole per generare un video di 3 minuti?',
    'faq.q1.answer': 'Ci vogliono circa 40 minuti per ottenere il video in modalità automatica.',
    'faq.q2.question': 'Qual è la differenza tra la modalità automatica e quella manuale?',
    'faq.q2.answer_intro': 'Entrambe le modalità offrono diversi livelli di controllo:',
    'faq.q2.auto_title': 'Modalità automatica',
    'faq.q2.auto_desc': 'Segui l\'avanzamento della generazione senza alcun intervento da parte tua.',
    'faq.q2.manual_title': 'Modalità manuale',
    'faq.q2.manual_desc': 'Puoi modificare e validare lo script, visualizzare le immagini di ogni scena e modificarne fino a 3, e infine visualizzare i video di ogni scena, cambiare l\'ordine e rigenerare fino a 3 video.',
    'faq.q3.question': 'Cosa include l\'abbonamento aggiuntivo "Director"?',
    'faq.q3.answer': 'Oltre a scaricare il video finale, potrai scaricare tutti gli elementi che compongono il video:',
    'faq.q3.item1': 'Lo script completo del progetto',
    'faq.q3.item2': 'Tutte le immagini generate per ogni scena',
    'faq.q3.item3': 'Tutti i video intermedi',
    'faq.q4.question': 'Quale formato video posso produrre?',
    'faq.q4.answer': 'Puoi creare video in formato 16:9 (orizzontale), 9:16 (verticale) o 1:1 (quadrato). Il file di output è in formato MP4.',
    'faq.q5.question': 'È possibile annullare un progetto?',
    'faq.q5.answer': 'Sì, ma a seconda della fase al momento dell\'annullamento, verranno addebitati dei costi: 1 credito se annulli prima della generazione dei video, e se i video sono già stati generati, è la durata dei video generati a determinare il numero di crediti consumati.',
    'faq.q6.question': 'Un progetto può essere recuperato se l\'ho eliminato?',
    'faq.q6.answer': 'No, i progetti eliminati non possono essere recuperati. Assicurati di scaricare tutti i file desiderati prima di eliminare un progetto.',
    'faq.more_questions': 'Hai altre domande?',
    'faq.contact_us': 'Il nostro team è qui per aiutarti.',
    'faq.contact_button': 'Contattaci',
    
    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Benvenuto',
    'dashboard.credits': 'Crediti disponibili',
    'dashboard.projects': 'I miei progetti',
    'dashboard.new_project': 'Nuovo progetto',
    'dashboard.no_projects': 'Nessun progetto',
    'dashboard.create_first': 'Crea il tuo primo video musicale!',
    'dashboard.payment_success': 'Pagamento riuscito! I tuoi crediti sono stati aggiunti.',
    
    // Project
    'project.title': 'Titolo del progetto',
    'project.language': 'Lingua',
    'project.format': 'Formato video',
    'project.format.landscape': 'Orizzontale (16:9)',
    'project.format.portrait': 'Verticale (9:16)',
    'project.format.square': 'Quadrato (1:1)',
    'project.style': 'Stile visivo',
    'project.mood': 'Mood / Descrizione',
    'project.audio': 'File audio',
    'project.srt': 'Sottotitoli (opzionale)',
    'project.intro': 'Intro video (opzionale)',
    'project.outro': 'Outro video (opzionale)',
    'project.photo': 'Foto personaggio (opzionale)',
    'project.mode': 'Modalità di creazione',
    'project.mode.auto': 'Automatico',
    'project.mode.manual': 'Manuale (passo dopo passo)',
    'project.public': 'Pubblica nella galleria',
    'project.create': 'Crea progetto',
    'project.no_credits': 'Non hai crediti. Acquistane alcuni per creare un progetto.',
    'project.error_no_audio': 'Seleziona un file audio',
    'project.error_no_title': 'Inserisci un titolo',
    'project.start': 'Avvia generazione',
    'project.cancel': 'Annulla',
    'project.delete': 'Elimina',
    'project.download_assets': 'Scarica asset',
    'project.download_assets_desc': 'Script, immagini e video (ZIP)',
    'project.download_assets_success': 'Archivio creato con successo',
    'project.download_assets_error': 'Errore nella creazione dell\'archivio',
    'project.director_required': 'Abbonamento Director richiesto',
    'project.director_required_desc': 'Abbonati a Director per scaricare tutti i file del progetto',
    'project.active_warning.title': 'Progetto in corso',
    'project.active_warning.message': 'Hai già un progetto in generazione: "{title}". Non puoi avviare un nuovo progetto finché questo non è completato.',
    'project.active_warning.view_project': 'Visualizza progetto corrente',
    
    // Script Editor
    'script.title': 'Editor di script',
    'script.scenes': 'scene',
    'script.total_duration': 'durata totale',
    'script.add_scene': 'Aggiungi scena',
    'script.validate_and_generate': 'Valida e genera immagini',
    'script.description': 'Descrizione',
    'script.image_prompt': 'Prompt immagine',
    'script.video_prompt': 'Prompt video',
    'script.duration_s': 'Durata (secondi)',
    'script.edit_scene': 'Modifica scena',
    'script.description_placeholder': 'Descrizione narrativa della scena...',
    'script.image_prompt_placeholder': 'Prompt dettagliato per generazione immagine (in inglese)...',
    'script.video_prompt_placeholder': 'Prompt dettagliato per animazione video (in inglese)...',
    'script.scene_updated': 'Scena aggiornata',
    'script.scene_added': 'Scena aggiunta',
    'script.scene_deleted': 'Scena eliminata',
    'script.error_update': 'Errore durante l\'aggiornamento',
    'script.error_create': 'Errore durante la creazione',
    'script.fill_all_fields': 'Si prega di compilare tutti i campi obbligatori',
    'script.confirm_delete_title': 'Elimina scena',
    'script.confirm_delete_message': 'Sei sicuro di voler eliminare questa scena?',
    'script.confirm_validate_title': 'Valida script',
    'script.confirm_validate_message': 'Validare lo script avvierà la generazione delle immagini. Questo consumerà crediti. Continuare?',
    'script.validate': 'Valida',
    'script.add': 'Aggiungi',
    'script.no_scenes': 'Nessuna scena nello script',
    'script.awaiting_validation': 'Lo script è pronto. Modificalo se necessario poi valida per avviare la generazione delle immagini.',
    
    // Image Validation
    'images.title': 'Validazione immagini',
    'images.images': 'immagini',
    'images.regenerations_left': 'rigenerazioni rimanenti',
    'images.instructions': 'Clicca su un\'immagine per modificarla, rigenerarla o sostituirla. Poi valida per avviare la generazione dei video.',
    'images.click_to_edit': 'Clicca per modificare',
    'images.edit_image': 'Modifica immagine',
    'images.no_image': 'Nessuna immagine',
    'images.regenerate': 'Rigenera',
    'images.remaining': 'rimanenti',
    'images.regeneration_limit_reached': 'Limite di rigenerazione raggiunto (massimo 3)',
    'images.prompt_required': 'Il prompt dell\'immagine (in inglese) è obbligatorio',
    'images.upload_replacement': 'Carica immagine',
    'images.upload_hint': 'Nessun limite sui caricamenti',
    'images.confirm_regenerate_title': 'Rigenera immagine',
    'images.confirm_regenerate_message': 'Vuoi rigenerare questa immagine? Ti rimangono {remaining} rigenerazione/i.',
    'images.regeneration_started': 'Rigenerazione avviata',
    'images.invalid_file_type': 'Tipo di file non valido. Carica un\'immagine.',
    'images.file_too_large': 'File troppo grande (max 10 MB)',
    'images.upload_success': 'Immagine caricata con successo',
    'images.upload_error': 'Errore durante il caricamento',
    'images.confirm_validate_title': 'Valida immagini',
    'images.confirm_validate_message': 'Validare le immagini avvierà la generazione dei video. Questo consumerà crediti. Continuare?',
    'images.validate_and_generate': 'Valida e genera video',
    'images.awaiting_validation': 'Le immagini sono pronte. Controllale e modificale se necessario, poi valida per avviare la generazione dei video.',
    
    // Video Validation
    'videos.title': 'Validazione video',
    'videos.videos': 'video',
    'videos.regenerations_left': 'rigenerazioni rimanenti',
    'videos.instructions': 'Clicca su un video per modificarlo o rigenerarlo. Usa le frecce per riordinare le scene. Poi valida per avviare la fusione finale.',
    'videos.click_to_edit': 'Clicca per modificare',
    'videos.edit_video': 'Modifica video',
    'videos.no_video': 'Nessun video',
    'videos.regenerate': 'Rigenera',
    'videos.remaining': 'rimanenti',
    'videos.regeneration_limit_reached': 'Limite di rigenerazione raggiunto (massimo 3)',
    'videos.confirm_regenerate_title': 'Rigenera video',
    'videos.confirm_regenerate_message': 'Vuoi rigenerare questo video? Ti rimangono {remaining} rigenerazione/i.',
    'videos.regeneration_started': 'Rigenerazione avviata',
    'videos.scene_number': 'Numero scena',
    'videos.duration': 'Durata',
    'videos.move_left': 'Sposta a sinistra',
    'videos.move_right': 'Sposta a destra',
    'videos.scenes_swapped': 'Scene scambiate',
    'videos.confirm_validate_title': 'Valida video',
    'videos.confirm_validate_message': 'Validare i video avvierà la fusione finale e l\'aggiunta dell\'audio. Continuare?',
    'videos.validate_and_merge': 'Valida e unisci',
    'videos.awaiting_validation': 'I video sono pronti. Riordina se necessario, poi valida per avviare la fusione finale con l\'audio.',
    
    // Status (Project)
    'status.draft': 'Bozza',
    'status.pending': 'In attesa',
    'status.processing': 'In elaborazione',
    'status.script_ready': 'Script pronto',
    'status.images_ready': 'Immagini pronte',
    'status.videos_ready': 'Video pronti',
    'status.completed': 'Completato',
    'status.failed': 'Fallito',
    'status.cancelled': 'Annullato',
    // Status (Scene)
    'status.image_generating': 'Generazione immagine...',
    'status.image_ready': 'Immagine pronta',
    'status.video_generating': 'Generazione video...',
    'status.video_ready': 'Video pronto',
    
    // Wizard
    'wizard.step1': 'Script',
    'wizard.step2': 'Immagini',
    'wizard.step3': 'Video',
    'wizard.step4': 'Fusione',
    'wizard.step5': 'Audio',
    'wizard.validate': 'Valida e continua',
    'wizard.regenerate': 'Rigenera',
    'wizard.edit': 'Modifica',
    
    // Auth
    'auth.login_with_google': 'Continua con Google',
    'auth.login_with_facebook': 'Continua con Facebook',
    'auth.or': 'o',
    'auth.dev_login': 'Login sviluppatore',
    
    // Profile
    'profile.title': 'Il mio profilo',
    'profile.display_name': 'Nome visualizzato',
    'profile.language': 'Lingua',
    'profile.theme': 'Tema',
    'profile.theme.dark': 'Scuro',
    'profile.theme.light': 'Chiaro',
    'profile.save': 'Salva',
    'profile.saved': 'Salvato',
    'profile.transactions': 'Cronologia transazioni',
    'profile.no_transactions': 'Nessuna transazione',
    'profile.subscription.title': 'Abbonamento Director',
    'profile.subscription.active': 'Abbonamento attivo',
    'profile.subscription.canceling': 'Cancellazione programmata',
    'profile.subscription.renews_on': 'Si rinnova il {date}',
    'profile.subscription.ends_on': 'Termina il {date}',
    'profile.subscription.cancel': 'Annulla abbonamento',
    'profile.subscription.cancel_title': 'Annullare l\'abbonamento?',
    'profile.subscription.cancel_confirm': 'Sei sicuro di voler annullare il tuo abbonamento Director? Manterrai l\'accesso fino alla fine del periodo corrente.',
    'profile.subscription.confirm_cancel': 'Sì, annulla',
    'profile.subscription.cancelled': 'Abbonamento annullato',
    'profile.subscription.cancel_error': 'Errore durante l\'annullamento',
    'profile.subscription.reactivate': 'Riattiva abbonamento',
    'profile.subscription.reactivated': 'Abbonamento riattivato',
    'profile.subscription.reactivate_error': 'Errore durante la riattivazione',
    'profile.subscription.manage_payment': 'Gestisci pagamento',
    'profile.subscription.portal_error': 'Errore di accesso al portale',
    'profile.subscription.not_subscribed': 'Non sei ancora abbonato a Director',
    'profile.subscription.subscribe_now': 'Abbonati ora',
    'profile.subscription_success': 'Abbonamento Director attivato con successo!',
    'profile.billing.title': 'Fatturazione',
    'profile.billing.description': 'Accedi alle tue fatture, ricevute di carta e gestisci i tuoi metodi di pagamento.',
    'profile.billing.access_portal': 'Il mio account Stripe',
    'profile.billing.portal_error': 'Impossibile accedere al portale di fatturazione',
    
    // Common
    'common.loading': 'Caricamento...',
    'common.error': 'Si è verificato un errore',
    'common.retry': 'Riprova',
    'common.save': 'Salva',
    'common.cancel': 'Annulla',
    'common.delete': 'Elimina',
    'common.confirm': 'Conferma',
    'common.back': 'Indietro',
    'common.next': 'Avanti',
    'common.download': 'Scarica',
    'common.share': 'Condividi',
    'common.close': 'Chiudi',
    'common.edit': 'Modifica',
    
    // Project View
    'project.not_found': 'Progetto non trovato',
    'project.back_to_dashboard': 'Torna alla dashboard',
    'project.processing': 'Elaborazione... {progress}%',
    'project.your_video': 'Il tuo video',
    'project.scenes_count': 'Scene ({count})',
    'project.scene_number': 'Scena {number}',
    'project.confirm_delete_title': 'Conferma eliminazione',
    'project.confirm_delete_message': 'Sei sicuro di voler eliminare questo progetto? Questa azione è irreversibile.',
    // Project Create
    'project.create_subtitle': 'Crea il tuo video musicale in pochi clic',
    'project.drop_audio': 'Trascina il tuo file audio qui',
    'project.audio_formats': 'MP3, WAV, OGG...',
    'project.section_info': 'Informazioni',
    'project.section_mode': 'Modalità di creazione',
    'project.section_files': 'File opzionali',
    'project.mode.auto_desc': 'L\'IA genera tutto automaticamente',
    'project.mode.manual_desc': 'Controlla ogni fase della creazione',
    'project.add_subtitles': 'Aggiungi sottotitoli',
    'project.add_photo': 'Aggiungi foto personaggio',
    'project.add_intro': 'Aggiungi video intro',
    'project.add_outro': 'Aggiungi video outro',
    'project.available_credits': 'Crediti disponibili',
    
    // Profile
    'profile.section_info': 'Informazioni',
  },
  
  de: {
    // Navigation
    'nav.home': 'Startseite',
    'nav.gallery': 'Galerie',
    'nav.pricing': 'Preise',
    'nav.dashboard': 'Dashboard',
    'nav.login': 'Anmelden',
    'nav.logout': 'Abmelden',
    'nav.profile': 'Profil',
    
    // Footer
    'footer.tagline': 'Erstelle beeindruckende KI-generierte Musikvideos',
    'footer.navigation': 'Navigation',
    'footer.support': 'Support',
    'footer.faq': 'FAQ',
    'footer.legal': 'Rechtliches',
    'footer.terms': 'Nutzungsbedingungen',
    'footer.privacy': 'Datenschutz',
    'footer.contact': 'Kontakt',
    
    // Home
    'home.hero.title': 'Verwandle deine Musik in Video',
    'home.hero.subtitle': 'Erstelle beeindruckende KI-generierte Musikvideos aus deinen Audiotracks in Minuten',
    'home.hero.cta': 'Starten',
    'home.hero.watch': 'Galerie ansehen',
    'home.demo.watch': 'Demo ansehen',
    'home.features.title': 'Wie es funktioniert',
    'home.features.upload.title': 'Lade dein Audio hoch',
    'home.features.upload.desc': 'Lade deine MP3, WAV oder jedes andere Audioformat hoch',
    'home.features.ai.title': 'Die KI erstellt das Skript',
    'home.features.ai.desc': 'Unsere KI analysiert dein Audio und generiert ein einzigartiges visuelles Szenario',
    'home.features.video.title': 'Erhalte dein Video',
    'home.features.video.desc': 'Erhalte ein professionelles HD-Musikvideo',
    'home.why.title': 'Warum M2V.io?',
    'home.why.fast': 'Schnell',
    'home.why.fast.desc': 'Generierung in Minuten, nicht Stunden',
    'home.why.quality': 'HD Qualität',
    'home.why.quality.desc': 'HD-Videos bereit für Social Media',
    'home.why.ai': 'Fortgeschrittene KI',
    'home.why.ai.desc': 'Die neuesten GPT-4, DALL-E 3 und MiniMax Modelle',
    'home.why.formats': 'Alle Formate',
    'home.why.formats.desc': 'Querformat, Hochformat, Quadrat für alle Bedürfnisse',
    'home.cta.title': 'Bereit dein erstes Video zu erstellen?',
    'home.cta.subtitle': 'Registriere dich und starte jetzt',
    
    // Gallery
    'gallery.title': 'Öffentliche Galerie',
    'gallery.subtitle': 'Entdecke Kreationen unserer Community',
    'gallery.empty': 'Die Galerie ist momentan leer',
    'gallery.empty.desc': 'Sei der Erste, der seine Kreation teilt!',
    'gallery.mine': 'Meine Videos',
    'gallery.filter.all': 'Alle Sprachen',
    
    // Pricing
    'pricing.title': 'Einfache und transparente Preise',
    'pricing.subtitle': 'Zahle nur für das, was du nutzt, kein Abo',
    'pricing.credit': 'Credit',
    'pricing.credits': 'Credits',
    'pricing.per_credit': 'pro Credit',
    'pricing.duration': '30 Sekunden Video',
    'pricing.buy': 'Kaufen',
    'pricing.popular': 'Beliebt',
    'pricing.how.title': 'Wie funktionieren Credits?',
    'pricing.how.desc': 'Ein Credit ermöglicht dir, 30 Sekunden Video zu generieren. Credits sind nicht teilbar: Ein 35-Sekunden-Video verbraucht 2 Credits.',
    'pricing.example': 'Beispiel: Für einen 3-Minuten-Song (180 Sekunden) brauchst du 6 Credits.',
    'pricing.included.title': 'In jedem Credit enthalten',
    'pricing.included.script': 'KI-generiertes Skript',
    'pricing.included.images': 'Hochwertige Bilder',
    'pricing.included.video': 'Animiertes HD-Video',
    'pricing.included.audio': 'Audio-Synchronisation',
    'pricing.included.download': 'Unbegrenzter Download',
    'pricing.included.formats': 'Alle Formate (16:9, 9:16, 1:1)',
    'pricing.included.no_watermark': 'Kein Wasserzeichen',
    'pricing.included.intro_video': 'Benutzerdefiniertes Intro-Video',
    'pricing.select_credits': 'Wähle deine Credits',
    'pricing.of_video': 'Video',
    'pricing.over_slider_max': 'Wert über {max} Credits',
    
    // Director subscription
    'pricing.director.title': 'Director',
    'pricing.director.description': 'Zugriff auf alle Dateien deiner Projekte',
    'pricing.director.month': 'Monat',
    'pricing.director.subscribe': 'Director abonnieren',
    'pricing.director.active': 'Aktives Abonnement',
    'pricing.director.canceling': 'Abonnement wird gekündigt',
    'pricing.director.manage': 'Mein Abonnement verwalten',
    'pricing.director.features.download_assets': 'Alle Projekt-Assets herunterladen',
    'pricing.director.features.script_json': 'Vollständiges Skript in JSON',
    'pricing.director.features.all_images': 'Alle generierten Bilder (PNG)',
    'pricing.director.features.all_videos': 'Alle Zwischenvideos (MP4)',
    'pricing.director.features.zip_archive': 'Vollständiges ZIP-Archiv',
    
    // Terms
    'terms.title': 'Nutzungsbedingungen',
    'terms.last_update': 'Letzte Aktualisierung',
    'terms.acceptance.title': 'Annahme der Bedingungen',
    'terms.acceptance.content': 'Durch den Zugriff auf und die Nutzung von M2V.io erklärst du dich mit diesen Nutzungsbedingungen einverstanden. Wenn du diese Bedingungen nicht akzeptierst, nutze unseren Service bitte nicht.',
    'terms.service.title': 'Servicebeschreibung',
    'terms.service.content': 'M2V.io ist eine KI-gestützte Plattform zur Erstellung von Musikvideos. Unser Service ermöglicht es Nutzern:',
    'terms.service.item1': 'Audiodateien hochzuladen',
    'terms.service.item2': 'Visuelle Skripte mittels KI zu generieren',
    'terms.service.item3': 'Animierte Videos aus generierten Bildern zu erstellen',
    'terms.service.item4': 'Erstellte Videos herunterzuladen und zu teilen',
    'terms.credits.title': 'Credit-System',
    'terms.credits.content': 'Die Nutzung des Services erfordert Credits. Es gelten folgende Bedingungen:',
    'terms.credits.item1': 'Ein Credit ermöglicht 30 Sekunden Videogenerierung',
    'terms.credits.item2': 'Credits sind nicht teilbar und nicht erstattungsfähig',
    'terms.credits.item3': 'Credits verfallen nicht und bleiben mit deinem Konto verbunden',
    'terms.content.title': 'Nutzerinhalte',
    'terms.content.content': 'Du bist verantwortlich für die Inhalte, die du hochlädst. Es ist verboten, folgendes hochzuladen:',
    'terms.content.item1': 'Urheberrechtlich geschützte Inhalte ohne Genehmigung',
    'terms.content.item2': 'Illegale, verleumderische oder beleidigende Inhalte',
    'terms.content.item3': 'Inhalte mit Viren oder Malware',
    'terms.intellectual.title': 'Geistiges Eigentum',
    'terms.intellectual.content': 'Du behältst alle Rechte an den Audio-Inhalten, die du hochlädst. Generierte Videos gehören dir und du kannst sie frei für persönliche oder kommerzielle Zwecke nutzen. M2V.io behält sich das Recht vor, in der öffentlichen Galerie veröffentlichte Videos zu Werbezwecken zu nutzen.',
    'terms.liability.title': 'Haftungsbeschränkung',
    'terms.liability.content': 'M2V.io wird "wie besehen" ohne Gewährleistung jeglicher Art bereitgestellt. Wir sind nicht verantwortlich für direkte oder indirekte Schäden, die aus der Nutzung unseres Services entstehen. Die Ergebnisse der KI-Generierung können variieren und werden nicht garantiert.',
    'terms.termination.title': 'Kündigung',
    'terms.termination.content': 'Wir behalten uns das Recht vor, dein Konto bei Verstoß gegen diese Bedingungen zu sperren oder zu kündigen. Bei Kündigung verlierst du den Zugang zu deinem Konto, behältst aber bereits heruntergeladene Videos.',
    'terms.modifications.title': 'Änderungen',
    'terms.modifications.content': 'Wir behalten uns das Recht vor, diese Bedingungen jederzeit zu ändern. Änderungen werden nach Veröffentlichung auf dieser Seite wirksam. Deine fortgesetzte Nutzung des Services nach Änderung gilt als Annahme der neuen Bedingungen.',
    'terms.contact.title': 'Kontakt',
    'terms.contact.content': 'Bei Fragen zu diesen Nutzungsbedingungen kontaktiere uns bitte unter:',
    
    // Privacy
    'privacy.title': 'Datenschutzrichtlinie',
    'privacy.last_update': 'Letzte Aktualisierung',
    'privacy.intro.title': 'Einleitung',
    'privacy.intro.content': 'M2V.io verpflichtet sich zum Schutz deiner Privatsphäre. Diese Richtlinie erklärt, wie wir deine personenbezogenen Daten gemäß DSGVO erheben, verwenden und schützen.',
    'privacy.collected.title': 'Erhobene Daten',
    'privacy.collected.content': 'Wir erheben folgende Daten:',
    'privacy.collected.item1': 'Kontoinformationen: E-Mail, Anzeigename, Profilbild (über Google/Facebook)',
    'privacy.collected.item2': 'Nutzungsdaten: erstellte Projekte, hochgeladene Dateien, Einstellungen',
    'privacy.collected.item3': 'Zahlungsdaten: verarbeitet durch Stripe, wir speichern keine Kartendaten',
    'privacy.collected.item4': 'Technische Daten: IP-Adresse, Browsertyp, Verbindungsprotokolle',
    'privacy.usage.title': 'Datennutzung',
    'privacy.usage.content': 'Deine Daten werden verwendet für:',
    'privacy.usage.item1': 'Bereitstellung und Verbesserung unserer Services',
    'privacy.usage.item2': 'Zahlungsabwicklung und Kontoverwaltung',
    'privacy.usage.item3': 'Wichtige Mitteilungen (kein Spam)',
    'privacy.usage.item4': 'Nutzungsanalyse zur Serviceverbesserung',
    'privacy.storage.title': 'Datenspeicherung',
    'privacy.storage.content': 'Deine persönlichen Daten werden gespeichert, solange dein Konto aktiv ist. Audio- und Videodateien werden 12 Monate nach Erstellung gespeichert. Du kannst jederzeit die Löschung deiner Daten beantragen.',
    'privacy.sharing.title': 'Datenweitergabe',
    'privacy.sharing.content': 'Wir verkaufen deine Daten niemals. Wir teilen sie nur mit:',
    'privacy.sharing.item1': 'Unseren technischen Anbietern (Hosting, Zahlung) unter Vertraulichkeitsvereinbarung',
    'privacy.sharing.item2': 'Behörden, wenn gesetzlich vorgeschrieben',
    'privacy.cookies.title': 'Cookies',
    'privacy.cookies.content': 'Wir verwenden essentielle Cookies für den Sitebetrieb (Authentifizierung, Einstellungen). Wir verwenden keine Werbe-Tracking-Cookies.',
    'privacy.rights.title': 'Deine Rechte',
    'privacy.rights.content': 'Gemäß DSGVO hast du folgende Rechte:',
    'privacy.rights.item1': 'Auskunftsrecht: eine Kopie deiner Daten erhalten',
    'privacy.rights.item2': 'Berichtigungsrecht: deine Daten korrigieren',
    'privacy.rights.item3': 'Löschungsrecht: deine Daten löschen',
    'privacy.rights.item4': 'Datenübertragbarkeit: deine Daten exportieren',
    'privacy.security.title': 'Sicherheit',
    'privacy.security.content': 'Wir implementieren angemessene Sicherheitsmaßnahmen zum Schutz deiner Daten: HTTPS-Verschlüsselung, sichere Authentifizierung, eingeschränkter Datenzugriff.',
    'privacy.contact.title': 'DSB Kontakt',
    'privacy.contact.content': 'Zur Ausübung deiner Rechte oder bei Fragen zum Datenschutz kontaktiere unseren Datenschutzbeauftragten:',
    
    // Contact
    'contact.title': 'Kontaktiere uns',
    'contact.subtitle': 'Eine Frage, ein Vorschlag? Wir sind hier, um zu helfen.',
    'contact.response_time': 'Antwortzeit',
    'contact.response_delay': 'Innerhalb von 24-48 Geschäftsstunden',
    'contact.languages': 'Unterstützte Sprachen',
    'contact.form.name': 'Name',
    'contact.form.name_placeholder': 'Dein Name',
    'contact.form.email': 'E-Mail',
    'contact.form.email_placeholder': 'deine@email.de',
    'contact.form.subject': 'Betreff',
    'contact.form.subject_placeholder': 'Wähle ein Thema',
    'contact.form.message': 'Nachricht',
    'contact.form.message_placeholder': 'Beschreibe deine Anfrage im Detail...',
    'contact.form.captcha': 'Anti-Spam-Verifizierung',
    'contact.form.captcha_hint': 'Lösen Sie diese einfache Berechnung, um zu beweisen, dass Sie ein Mensch sind.',
    'contact.form.captcha_placeholder': 'Antwort',
    'contact.form.captcha_refresh': 'Neue Berechnung',
    'contact.form.send': 'Nachricht senden',
    'contact.form.invalid': 'Bitte fülle alle Felder korrekt aus',
    'contact.form.success': 'Nachricht erfolgreich gesendet!',
    'contact.form.error': 'Fehler beim Senden, bitte versuche es erneut',
    'contact.form.sent_title': 'Nachricht gesendet!',
    'contact.form.sent_message': 'Wir haben deine Nachricht erhalten und werden schnellstmöglich antworten.',
    'contact.form.new_message': 'Weitere Nachricht senden',
    'contact.subject.general': 'Allgemeine Frage',
    'contact.subject.support': 'Technischer Support',
    'contact.subject.billing': 'Abrechnung',
    'contact.subject.partnership': 'Partnerschaft',
    'contact.subject.other': 'Sonstiges',
    
    // FAQ
    'faq.title': 'Häufig gestellte Fragen',
    'faq.subtitle': 'Finden Sie schnell Antworten auf Ihre häufigsten Fragen',
    'faq.q1.question': 'Wie lange dauert es, ein 3-minütiges Video zu erstellen?',
    'faq.q1.answer': 'Sie sollten etwa 40 Minuten einplanen, um das Video im automatischen Modus zu erhalten.',
    'faq.q2.question': 'Was ist der Unterschied zwischen automatischem und manuellem Modus?',
    'faq.q2.answer_intro': 'Beide Modi bieten unterschiedliche Kontrollebenen:',
    'faq.q2.auto_title': 'Automatischer Modus',
    'faq.q2.auto_desc': 'Sie verfolgen den Fortschritt der Generierung ohne jeglichen Eingriff Ihrerseits.',
    'faq.q2.manual_title': 'Manueller Modus',
    'faq.q2.manual_desc': 'Sie können das Skript bearbeiten und validieren, die Bilder jeder Szene anzeigen und bis zu 3 ändern, und schließlich die Videos jeder Szene anzeigen, die Reihenfolge ändern und bis zu 3 Videos neu generieren.',
    'faq.q3.question': 'Was beinhaltet das zusätzliche "Director"-Abonnement?',
    'faq.q3.answer': 'Zusätzlich zum Download des finalen Videos können Sie alle Elemente herunterladen, aus denen das Video besteht:',
    'faq.q3.item1': 'Das vollständige Projektskript',
    'faq.q3.item2': 'Alle für jede Szene generierten Bilder',
    'faq.q3.item3': 'Alle Zwischenvideos',
    'faq.q4.question': 'Welches Videoformat kann ich erstellen?',
    'faq.q4.answer': 'Sie können Videos im Format 16:9 (Querformat), 9:16 (Hochformat) oder 1:1 (Quadrat) erstellen. Die Ausgabedatei ist im MP4-Format.',
    'faq.q5.question': 'Ist es möglich, ein Projekt abzubrechen?',
    'faq.q5.answer': 'Ja, aber je nach Phase zum Zeitpunkt der Stornierung werden Gebühren erhoben: 1 Credit, wenn Sie vor der Videogenerierung abbrechen, und wenn bereits Videos generiert wurden, bestimmt die Dauer der generierten Videos die Anzahl der verbrauchten Credits.',
    'faq.q6.question': 'Kann ein Projekt wiederhergestellt werden, wenn ich es gelöscht habe?',
    'faq.q6.answer': 'Nein, gelöschte Projekte können nicht wiederhergestellt werden. Stellen Sie sicher, dass Sie alle gewünschten Dateien herunterladen, bevor Sie ein Projekt löschen.',
    'faq.more_questions': 'Haben Sie weitere Fragen?',
    'faq.contact_us': 'Unser Team ist hier, um Ihnen zu helfen.',
    'faq.contact_button': 'Kontaktieren Sie uns',
    
    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Willkommen',
    'dashboard.credits': 'Verfügbare Credits',
    'dashboard.projects': 'Meine Projekte',
    'dashboard.new_project': 'Neues Projekt',
    'dashboard.no_projects': 'Keine Projekte',
    'dashboard.create_first': 'Erstelle dein erstes Musikvideo!',
    'dashboard.payment_success': 'Zahlung erfolgreich! Deine Credits wurden hinzugefügt.',
    
    // Project
    'project.title': 'Projekttitel',
    'project.language': 'Sprache',
    'project.format': 'Videoformat',
    'project.format.landscape': 'Querformat (16:9)',
    'project.format.portrait': 'Hochformat (9:16)',
    'project.format.square': 'Quadrat (1:1)',
    'project.style': 'Visueller Stil',
    'project.mood': 'Stimmung / Beschreibung',
    'project.audio': 'Audiodatei',
    'project.srt': 'Untertitel (optional)',
    'project.intro': 'Video-Intro (optional)',
    'project.outro': 'Video-Outro (optional)',
    'project.photo': 'Charakterfoto (optional)',
    'project.mode': 'Erstellungsmodus',
    'project.mode.auto': 'Automatisch',
    'project.mode.manual': 'Manuell (Schritt für Schritt)',
    'project.public': 'In Galerie veröffentlichen',
    'project.create': 'Projekt erstellen',
    'project.no_credits': 'Du hast keine Credits. Bitte kaufe welche, um ein Projekt zu erstellen.',
    'project.error_no_audio': 'Bitte wähle eine Audiodatei aus',
    'project.error_no_title': 'Bitte gib einen Titel ein',
    'project.start': 'Generierung starten',
    'project.cancel': 'Abbrechen',
    'project.delete': 'Löschen',
    'project.download_assets': 'Assets herunterladen',
    'project.download_assets_desc': 'Skript, Bilder und Videos (ZIP)',
    'project.download_assets_success': 'Archiv erfolgreich erstellt',
    'project.download_assets_error': 'Fehler beim Erstellen des Archivs',
    'project.director_required': 'Director-Abonnement erforderlich',
    'project.director_required_desc': 'Abonniere Director, um alle Projektdateien herunterzuladen',
    'project.active_warning.title': 'Projekt in Bearbeitung',
    'project.active_warning.message': 'Du hast bereits ein Projekt in Generierung: "{title}". Du kannst kein neues Projekt starten, bis dieses abgeschlossen ist.',
    'project.active_warning.view_project': 'Aktuelles Projekt anzeigen',
    
    // Script Editor
    'script.title': 'Skript-Editor',
    'script.scenes': 'Szenen',
    'script.total_duration': 'Gesamtdauer',
    'script.add_scene': 'Szene hinzufügen',
    'script.validate_and_generate': 'Validieren und Bilder generieren',
    'script.description': 'Beschreibung',
    'script.image_prompt': 'Bild-Prompt',
    'script.video_prompt': 'Video-Prompt',
    'script.duration_s': 'Dauer (Sekunden)',
    'script.edit_scene': 'Szene bearbeiten',
    'script.description_placeholder': 'Narrative Beschreibung der Szene...',
    'script.image_prompt_placeholder': 'Detaillierter Prompt für Bildgenerierung (auf Englisch)...',
    'script.video_prompt_placeholder': 'Detaillierter Prompt für Videoanimation (auf Englisch)...',
    'script.scene_updated': 'Szene aktualisiert',
    'script.scene_added': 'Szene hinzugefügt',
    'script.scene_deleted': 'Szene gelöscht',
    'script.error_update': 'Fehler beim Aktualisieren',
    'script.error_create': 'Fehler beim Erstellen',
    'script.fill_all_fields': 'Bitte füllen Sie alle Pflichtfelder aus',
    'script.confirm_delete_title': 'Szene löschen',
    'script.confirm_delete_message': 'Sind Sie sicher, dass Sie diese Szene löschen möchten?',
    'script.confirm_validate_title': 'Skript validieren',
    'script.confirm_validate_message': 'Die Validierung des Skripts startet die Bildgenerierung. Dies verbraucht Credits. Fortfahren?',
    'script.validate': 'Validieren',
    'script.add': 'Hinzufügen',
    'script.no_scenes': 'Keine Szenen im Skript',
    'script.awaiting_validation': 'Das Skript ist bereit. Bearbeiten Sie es bei Bedarf und validieren Sie dann, um die Bildgenerierung zu starten.',
    
    // Image Validation
    'images.title': 'Bildvalidierung',
    'images.images': 'Bilder',
    'images.regenerations_left': 'Regenerierungen übrig',
    'images.instructions': 'Klicken Sie auf ein Bild, um es zu bearbeiten, neu zu generieren oder zu ersetzen. Validieren Sie dann, um die Videogenerierung zu starten.',
    'images.click_to_edit': 'Klicken zum Bearbeiten',
    'images.edit_image': 'Bild bearbeiten',
    'images.no_image': 'Kein Bild',
    'images.regenerate': 'Regenerieren',
    'images.remaining': 'übrig',
    'images.regeneration_limit_reached': 'Regenerierungslimit erreicht (maximal 3)',
    'images.prompt_required': 'Der Bild-Prompt (auf Englisch) ist erforderlich',
    'images.upload_replacement': 'Bild hochladen',
    'images.upload_hint': 'Keine Beschränkung bei Uploads',
    'images.confirm_regenerate_title': 'Bild regenerieren',
    'images.confirm_regenerate_message': 'Möchten Sie dieses Bild regenerieren? Sie haben noch {remaining} Regenerierung(en).',
    'images.regeneration_started': 'Regenerierung gestartet',
    'images.invalid_file_type': 'Ungültiger Dateityp. Bitte laden Sie ein Bild hoch.',
    'images.file_too_large': 'Datei zu groß (max 10 MB)',
    'images.upload_success': 'Bild erfolgreich hochgeladen',
    'images.upload_error': 'Upload-Fehler',
    'images.confirm_validate_title': 'Bilder validieren',
    'images.confirm_validate_message': 'Das Validieren der Bilder startet die Videogenerierung. Dies verbraucht Credits. Fortfahren?',
    'images.validate_and_generate': 'Validieren und Videos generieren',
    'images.awaiting_validation': 'Die Bilder sind bereit. Überprüfen und bearbeiten Sie sie bei Bedarf, dann validieren Sie, um die Videogenerierung zu starten.',
    
    // Video Validation
    'videos.title': 'Videovalidierung',
    'videos.videos': 'Videos',
    'videos.regenerations_left': 'Regenerierungen übrig',
    'videos.instructions': 'Klicken Sie auf ein Video, um es zu bearbeiten oder neu zu generieren. Verwenden Sie die Pfeile, um die Szenenreihenfolge zu ändern. Validieren Sie dann, um die finale Zusammenführung zu starten.',
    'videos.click_to_edit': 'Klicken zum Bearbeiten',
    'videos.edit_video': 'Video bearbeiten',
    'videos.no_video': 'Kein Video',
    'videos.regenerate': 'Regenerieren',
    'videos.remaining': 'übrig',
    'videos.regeneration_limit_reached': 'Regenerierungslimit erreicht (maximal 3)',
    'videos.confirm_regenerate_title': 'Video regenerieren',
    'videos.confirm_regenerate_message': 'Möchten Sie dieses Video regenerieren? Sie haben noch {remaining} Regenerierung(en).',
    'videos.regeneration_started': 'Regenerierung gestartet',
    'videos.scene_number': 'Szenennummer',
    'videos.duration': 'Dauer',
    'videos.move_left': 'Nach links verschieben',
    'videos.move_right': 'Nach rechts verschieben',
    'videos.scenes_swapped': 'Szenen getauscht',
    'videos.confirm_validate_title': 'Videos validieren',
    'videos.confirm_validate_message': 'Das Validieren der Videos startet die finale Zusammenführung und Audioadditon. Fortfahren?',
    'videos.validate_and_merge': 'Validieren und zusammenführen',
    'videos.awaiting_validation': 'Die Videos sind bereit. Ordnen Sie bei Bedarf neu, dann validieren Sie, um die finale Zusammenführung mit Audio zu starten.',
    
    // Status (Project)
    'status.draft': 'Entwurf',
    'status.pending': 'Ausstehend',
    'status.processing': 'In Bearbeitung',
    'status.script_ready': 'Skript bereit',
    'status.images_ready': 'Bilder bereit',
    'status.videos_ready': 'Videos bereit',
    'status.completed': 'Abgeschlossen',
    'status.failed': 'Fehlgeschlagen',
    'status.cancelled': 'Abgebrochen',
    // Status (Scene)
    'status.image_generating': 'Bild wird generiert...',
    'status.image_ready': 'Bild bereit',
    'status.video_generating': 'Video wird generiert...',
    'status.video_ready': 'Video bereit',
    
    // Wizard
    'wizard.step1': 'Skript',
    'wizard.step2': 'Bilder',
    'wizard.step3': 'Videos',
    'wizard.step4': 'Zusammenfügen',
    'wizard.step5': 'Audio',
    'wizard.validate': 'Bestätigen und fortfahren',
    'wizard.regenerate': 'Neu generieren',
    'wizard.edit': 'Bearbeiten',
    
    // Auth
    'auth.login_with_google': 'Mit Google fortfahren',
    'auth.login_with_facebook': 'Mit Facebook fortfahren',
    'auth.or': 'oder',
    'auth.dev_login': 'Entwickler-Login',
    
    // Profile
    'profile.title': 'Mein Profil',
    'profile.display_name': 'Anzeigename',
    'profile.language': 'Sprache',
    'profile.saved': 'Gespeichert',
    'profile.no_transactions': 'Keine Transaktionen',
    'profile.subscription.title': 'Director Abonnement',
    'profile.subscription.active': 'Aktives Abonnement',
    'profile.subscription.canceling': 'Kündigung geplant',
    'profile.subscription.renews_on': 'Erneuert am {date}',
    'profile.subscription.ends_on': 'Endet am {date}',
    'profile.subscription.cancel': 'Abonnement kündigen',
    'profile.subscription.cancel_title': 'Abonnement kündigen?',
    'profile.subscription.cancel_confirm': 'Bist du sicher, dass du dein Director-Abonnement kündigen möchtest? Du behältst den Zugriff bis zum Ende des aktuellen Zeitraums.',
    'profile.subscription.confirm_cancel': 'Ja, kündigen',
    'profile.subscription.cancelled': 'Abonnement gekündigt',
    'profile.subscription.cancel_error': 'Fehler beim Kündigen',
    'profile.subscription.reactivate': 'Abonnement reaktivieren',
    'profile.subscription.reactivated': 'Abonnement reaktiviert',
    'profile.subscription.reactivate_error': 'Fehler beim Reaktivieren',
    'profile.subscription.manage_payment': 'Zahlung verwalten',
    'profile.subscription.portal_error': 'Fehler beim Portalzugriff',
    'profile.subscription.not_subscribed': 'Du hast noch kein Director-Abonnement',
    'profile.subscription.subscribe_now': 'Jetzt abonnieren',
    'profile.subscription_success': 'Director-Abonnement erfolgreich aktiviert!',
    'profile.billing.title': 'Rechnungsstellung',
    'profile.billing.description': 'Greifen Sie auf Ihre Rechnungen, Kartenbelege zu und verwalten Sie Ihre Zahlungsmethoden.',
    'profile.billing.access_portal': 'Mein Stripe-Konto',
    'profile.billing.portal_error': 'Zugriff auf das Rechnungsportal nicht möglich',
    'profile.theme': 'Thema',
    'profile.theme.dark': 'Dunkel',
    'profile.theme.light': 'Hell',
    'profile.save': 'Speichern',
    'profile.transactions': 'Transaktionsverlauf',
    
    // Common
    'common.loading': 'Laden...',
    'common.error': 'Ein Fehler ist aufgetreten',
    'common.retry': 'Erneut versuchen',
    'common.save': 'Speichern',
    'common.cancel': 'Abbrechen',
    'common.delete': 'Löschen',
    'common.confirm': 'Bestätigen',
    'common.back': 'Zurück',
    'common.next': 'Weiter',
    'common.download': 'Herunterladen',
    'common.share': 'Teilen',
    'common.close': 'Schließen',
    'common.edit': 'Bearbeiten',
    
    // Project View
    'project.not_found': 'Projekt nicht gefunden',
    'project.back_to_dashboard': 'Zurück zum Dashboard',
    'project.processing': 'Verarbeitung... {progress}%',
    'project.your_video': 'Dein Video',
    'project.scenes_count': 'Szenen ({count})',
    'project.scene_number': 'Szene {number}',
    'project.confirm_delete_title': 'Löschen bestätigen',
    'project.confirm_delete_message': 'Bist du sicher, dass du dieses Projekt löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.',
    // Project Create
    'project.create_subtitle': 'Erstelle dein Musikvideo mit wenigen Klicks',
    'project.drop_audio': 'Ziehe deine Audiodatei hierher',
    'project.audio_formats': 'MP3, WAV, OGG...',
    'project.section_info': 'Informationen',
    'project.section_mode': 'Erstellungsmodus',
    'project.section_files': 'Optionale Dateien',
    'project.mode.auto_desc': 'Die KI generiert alles automatisch',
    'project.mode.manual_desc': 'Kontrolliere jeden Schritt der Erstellung',
    'project.add_subtitles': 'Untertitel hinzufügen',
    'project.add_photo': 'Charakterfoto hinzufügen',
    'project.add_intro': 'Intro-Video hinzufügen',
    'project.add_outro': 'Outro-Video hinzufügen',
    'project.available_credits': 'Verfügbare Credits',
    
    // Profile
    'profile.section_info': 'Informationen',
  },
  
  pt: {
    // Navigation
    'nav.home': 'Início',
    'nav.gallery': 'Galeria',
    'nav.pricing': 'Preços',
    'nav.dashboard': 'Painel',
    'nav.login': 'Entrar',
    'nav.logout': 'Sair',
    'nav.profile': 'Perfil',
    
    // Footer
    'footer.tagline': 'Crie incríveis vídeos musicais gerados por IA',
    'footer.navigation': 'Navegação',
    'footer.support': 'Suporte',
    'footer.faq': 'Perguntas frequentes',
    'footer.legal': 'Legal',
    'footer.terms': 'Termos de serviço',
    'footer.privacy': 'Privacidade',
    'footer.contact': 'Contato',
    
    // Home
    'home.hero.title': 'Transforme sua música em vídeo',
    'home.hero.subtitle': 'Crie vídeos musicais incríveis gerados por IA a partir das suas faixas de áudio em minutos',
    'home.hero.cta': 'Começar',
    'home.hero.watch': 'Ver galeria',
    'home.demo.watch': 'Ver demonstração',
    'home.features.title': 'Como funciona',
    'home.features.upload.title': 'Envie seu áudio',
    'home.features.upload.desc': 'Envie seu arquivo MP3, WAV ou qualquer formato de áudio',
    'home.features.ai.title': 'A IA cria o roteiro',
    'home.features.ai.desc': 'Nossa IA analisa seu áudio e gera um cenário visual único',
    'home.features.video.title': 'Receba seu vídeo',
    'home.features.video.desc': 'Obtenha um vídeo musical profissional em HD',
    'home.why.title': 'Por que M2V.io?',
    'home.why.fast': 'Rápido',
    'home.why.fast.desc': 'Geração em minutos, não horas',
    'home.why.quality': 'Qualidade HD',
    'home.why.quality.desc': 'Vídeos em alta definição prontos para redes sociais',
    'home.why.ai': 'IA Avançada',
    'home.why.ai.desc': 'Os mais recentes modelos GPT-4, DALL-E 3 e MiniMax',
    'home.why.formats': 'Todos os formatos',
    'home.why.formats.desc': 'Paisagem, Retrato, Quadrado para todas as necessidades',
    'home.cta.title': 'Pronto para criar seu primeiro vídeo?',
    'home.cta.subtitle': 'Cadastre-se e comece agora',
    
    // Gallery
    'gallery.title': 'Galeria pública',
    'gallery.subtitle': 'Descubra as criações da nossa comunidade',
    'gallery.empty': 'A galeria está vazia no momento',
    'gallery.empty.desc': 'Seja o primeiro a compartilhar sua criação!',
    'gallery.mine': 'Meus vídeos',
    'gallery.filter.all': 'Todos os idiomas',
    
    // Pricing
    'pricing.title': 'Preços simples e transparentes',
    'pricing.subtitle': 'Pague apenas pelo que usar, sem assinatura',
    'pricing.credit': 'crédito',
    'pricing.credits': 'créditos',
    'pricing.per_credit': 'por crédito',
    'pricing.duration': '30 segundos de vídeo',
    'pricing.buy': 'Comprar',
    'pricing.popular': 'Popular',
    'pricing.how.title': 'Como funcionam os créditos?',
    'pricing.how.desc': 'Um crédito permite gerar 30 segundos de vídeo. Os créditos não são divisíveis: um vídeo de 35 segundos consumirá 2 créditos.',
    'pricing.example': 'Exemplo: Para uma música de 3 minutos (180 segundos), você precisará de 6 créditos.',
    'pricing.included.title': 'Incluído em cada crédito',
    'pricing.included.script': 'Roteiro gerado por IA',
    'pricing.included.images': 'Imagens de alta qualidade',
    'pricing.included.video': 'Vídeo HD animado',
    'pricing.included.audio': 'Sincronização de áudio',
    'pricing.included.download': 'Download ilimitado',
    'pricing.included.formats': 'Todos os formatos (16:9, 9:16, 1:1)',
    'pricing.included.no_watermark': 'Sem marca d\'água',
    'pricing.included.intro_video': 'Vídeo de intro personalizado',
    'pricing.select_credits': 'Escolha seus créditos',
    'pricing.of_video': 'de vídeo',
    'pricing.over_slider_max': 'Valor superior a {max} créditos',
    
    // Director subscription
    'pricing.director.title': 'Director',
    'pricing.director.description': 'Acesse todos os arquivos dos seus projetos',
    'pricing.director.month': 'mês',
    'pricing.director.subscribe': 'Assinar Director',
    'pricing.director.active': 'Assinatura ativa',
    'pricing.director.canceling': 'Assinatura sendo cancelada',
    'pricing.director.manage': 'Gerenciar minha assinatura',
    'pricing.director.features.download_assets': 'Baixar todos os assets do projeto',
    'pricing.director.features.script_json': 'Roteiro completo em JSON',
    'pricing.director.features.all_images': 'Todas as imagens geradas (PNG)',
    'pricing.director.features.all_videos': 'Todos os vídeos intermediários (MP4)',
    'pricing.director.features.zip_archive': 'Arquivo ZIP completo',
    
    // Terms
    'terms.title': 'Termos de serviço',
    'terms.last_update': 'Última atualização',
    'terms.acceptance.title': 'Aceitação dos termos',
    'terms.acceptance.content': 'Ao acessar e usar o M2V.io, você concorda em ficar vinculado a estes termos de serviço. Se você não aceitar estes termos, por favor não use nosso serviço.',
    'terms.service.title': 'Descrição do serviço',
    'terms.service.content': 'M2V.io é uma plataforma de geração de vídeos musicais assistida por inteligência artificial. Nosso serviço permite aos usuários:',
    'terms.service.item1': 'Enviar arquivos de áudio',
    'terms.service.item2': 'Gerar roteiros visuais por IA',
    'terms.service.item3': 'Criar vídeos animados a partir de imagens geradas',
    'terms.service.item4': 'Baixar e compartilhar os vídeos criados',
    'terms.credits.title': 'Sistema de créditos',
    'terms.credits.content': 'O uso do serviço requer créditos. As seguintes condições se aplicam:',
    'terms.credits.item1': 'Um crédito permite gerar 30 segundos de vídeo',
    'terms.credits.item2': 'Os créditos não são divisíveis e não são reembolsáveis',
    'terms.credits.item3': 'Os créditos não expiram e permanecem vinculados à sua conta',
    'terms.content.title': 'Conteúdo do usuário',
    'terms.content.content': 'Você é responsável pelo conteúdo que envia. É proibido enviar:',
    'terms.content.item1': 'Conteúdo protegido por direitos autorais sem autorização',
    'terms.content.item2': 'Conteúdo ilegal, difamatório ou ofensivo',
    'terms.content.item3': 'Conteúdo contendo vírus ou malware',
    'terms.intellectual.title': 'Propriedade intelectual',
    'terms.intellectual.content': 'Você mantém todos os direitos sobre o conteúdo de áudio que envia. Os vídeos gerados pertencem a você e você pode usá-los livremente para fins pessoais ou comerciais. O M2V.io reserva-se o direito de usar vídeos publicados na galeria pública para fins promocionais.',
    'terms.liability.title': 'Limitação de responsabilidade',
    'terms.liability.content': 'O M2V.io é fornecido "como está" sem garantia de qualquer tipo. Não somos responsáveis por danos diretos ou indiretos resultantes do uso do nosso serviço. Os resultados da geração por IA podem variar e não são garantidos.',
    'terms.termination.title': 'Rescisão',
    'terms.termination.content': 'Reservamo-nos o direito de suspender ou encerrar sua conta em caso de violação destes termos. Em caso de rescisão, você perderá o acesso à sua conta, mas manterá os vídeos já baixados.',
    'terms.modifications.title': 'Modificações',
    'terms.modifications.content': 'Reservamo-nos o direito de modificar estes termos a qualquer momento. As modificações serão efetivas após a publicação nesta página. Seu uso continuado do serviço após a modificação constitui aceitação dos novos termos.',
    'terms.contact.title': 'Contato',
    'terms.contact.content': 'Para qualquer pergunta sobre estes termos de serviço, entre em contato conosco em:',
    
    // Privacy
    'privacy.title': 'Política de privacidade',
    'privacy.last_update': 'Última atualização',
    'privacy.intro.title': 'Introdução',
    'privacy.intro.content': 'O M2V.io está comprometido em proteger sua privacidade. Esta política explica como coletamos, usamos e protegemos seus dados pessoais de acordo com o GDPR.',
    'privacy.collected.title': 'Dados coletados',
    'privacy.collected.content': 'Coletamos os seguintes dados:',
    'privacy.collected.item1': 'Informações da conta: email, nome de exibição, foto de perfil (via Google/Facebook)',
    'privacy.collected.item2': 'Dados de uso: projetos criados, arquivos enviados, preferências',
    'privacy.collected.item3': 'Dados de pagamento: processados pelo Stripe, não armazenamos informações do seu cartão',
    'privacy.collected.item4': 'Dados técnicos: endereço IP, tipo de navegador, logs de conexão',
    'privacy.usage.title': 'Uso dos dados',
    'privacy.usage.content': 'Seus dados são usados para:',
    'privacy.usage.item1': 'Fornecer e melhorar nossos serviços',
    'privacy.usage.item2': 'Processar seus pagamentos e gerenciar sua conta',
    'privacy.usage.item3': 'Enviar comunicações importantes (sem spam)',
    'privacy.usage.item4': 'Analisar o uso para melhorar o serviço',
    'privacy.storage.title': 'Retenção de dados',
    'privacy.storage.content': 'Seus dados pessoais são retidos enquanto sua conta estiver ativa. Os arquivos de áudio e vídeo são armazenados por 12 meses após a criação. Você pode solicitar a exclusão de seus dados a qualquer momento.',
    'privacy.sharing.title': 'Compartilhamento de dados',
    'privacy.sharing.content': 'Nunca vendemos seus dados. Só os compartilhamos com:',
    'privacy.sharing.item1': 'Nossos provedores técnicos (hospedagem, pagamento) sob acordo de confidencialidade',
    'privacy.sharing.item2': 'Autoridades se exigido por lei',
    'privacy.cookies.title': 'Cookies',
    'privacy.cookies.content': 'Usamos cookies essenciais para o funcionamento do site (autenticação, preferências). Não usamos cookies de rastreamento publicitário.',
    'privacy.rights.title': 'Seus direitos',
    'privacy.rights.content': 'De acordo com o GDPR, você tem os seguintes direitos:',
    'privacy.rights.item1': 'Direito de acesso: obter uma cópia dos seus dados',
    'privacy.rights.item2': 'Direito de retificação: corrigir seus dados',
    'privacy.rights.item3': 'Direito ao apagamento: excluir seus dados',
    'privacy.rights.item4': 'Direito à portabilidade: exportar seus dados',
    'privacy.security.title': 'Segurança',
    'privacy.security.content': 'Implementamos medidas de segurança apropriadas para proteger seus dados: criptografia HTTPS, autenticação segura, acesso restrito aos dados.',
    'privacy.contact.title': 'Contato DPO',
    'privacy.contact.content': 'Para exercer seus direitos ou para qualquer pergunta sobre a proteção dos seus dados, entre em contato com nosso encarregado de proteção de dados:',
    
    // Contact
    'contact.title': 'Entre em contato',
    'contact.subtitle': 'Uma pergunta, uma sugestão? Estamos aqui para ajudar.',
    'contact.response_time': 'Tempo de resposta',
    'contact.response_delay': 'Em 24-48 horas úteis',
    'contact.languages': 'Idiomas suportados',
    'contact.form.name': 'Nome',
    'contact.form.name_placeholder': 'Seu nome',
    'contact.form.email': 'Email',
    'contact.form.email_placeholder': 'seu@email.com',
    'contact.form.subject': 'Assunto',
    'contact.form.subject_placeholder': 'Selecione um assunto',
    'contact.form.message': 'Mensagem',
    'contact.form.message_placeholder': 'Descreva sua solicitação em detalhes...',
    'contact.form.captcha': 'Verificação anti-spam',
    'contact.form.captcha_hint': 'Resolva este cálculo simples para provar que você é humano.',
    'contact.form.captcha_placeholder': 'Resposta',
    'contact.form.captcha_refresh': 'Novo cálculo',
    'contact.form.send': 'Enviar mensagem',
    'contact.form.invalid': 'Por favor preencha todos os campos corretamente',
    'contact.form.success': 'Mensagem enviada com sucesso!',
    'contact.form.error': 'Erro ao enviar, por favor tente novamente',
    'contact.form.sent_title': 'Mensagem enviada!',
    'contact.form.sent_message': 'Recebemos sua mensagem e responderemos o mais rápido possível.',
    'contact.form.new_message': 'Enviar outra mensagem',
    'contact.subject.general': 'Pergunta geral',
    'contact.subject.support': 'Suporte técnico',
    'contact.subject.billing': 'Faturamento',
    'contact.subject.partnership': 'Parceria',
    'contact.subject.other': 'Outro',
    
    // FAQ
    'faq.title': 'Perguntas frequentes',
    'faq.subtitle': 'Encontre rapidamente as respostas para suas perguntas mais comuns',
    'faq.q1.question': 'Quanto tempo leva para gerar um vídeo de 3 minutos?',
    'faq.q1.answer': 'Você deve esperar cerca de 40 minutos para obter o vídeo no modo automático.',
    'faq.q2.question': 'Qual é a diferença entre o modo automático e o manual?',
    'faq.q2.answer_intro': 'Ambos os modos oferecem diferentes níveis de controle:',
    'faq.q2.auto_title': 'Modo automático',
    'faq.q2.auto_desc': 'Você acompanha o progresso da geração sem nenhuma intervenção da sua parte.',
    'faq.q2.manual_title': 'Modo manual',
    'faq.q2.manual_desc': 'Você pode editar e validar o roteiro, visualizar as imagens de cada cena e modificar até 3, e finalmente visualizar os vídeos de cada cena, alterar a ordem e regenerar até 3 vídeos.',
    'faq.q3.question': 'O que inclui a assinatura adicional "Director"?',
    'faq.q3.answer': 'Além de baixar o vídeo final, você poderá baixar todos os elementos que compõem o vídeo:',
    'faq.q3.item1': 'O roteiro completo do projeto',
    'faq.q3.item2': 'Todas as imagens geradas para cada cena',
    'faq.q3.item3': 'Todos os vídeos intermediários',
    'faq.q4.question': 'Qual formato de vídeo posso produzir?',
    'faq.q4.answer': 'Você pode criar vídeos no formato 16:9 (paisagem), 9:16 (retrato) ou 1:1 (quadrado). O arquivo de saída está no formato MP4.',
    'faq.q5.question': 'É possível cancelar um projeto?',
    'faq.q5.answer': 'Sim, mas dependendo da etapa no momento do cancelamento, serão cobradas taxas: 1 crédito se você cancelar antes da geração de vídeos, e se os vídeos já foram gerados, é a duração dos vídeos gerados que determinará o número de créditos consumidos.',
    'faq.q6.question': 'Um projeto pode ser recuperado se eu o excluí?',
    'faq.q6.answer': 'Não, projetos excluídos não podem ser recuperados. Certifique-se de baixar todos os arquivos desejados antes de excluir um projeto.',
    'faq.more_questions': 'Você tem mais perguntas?',
    'faq.contact_us': 'Nossa equipe está aqui para ajudá-lo.',
    'faq.contact_button': 'Entre em contato',
    
    // Dashboard
    'dashboard.title': 'Painel',
    'dashboard.welcome': 'Bem-vindo',
    'dashboard.credits': 'Créditos disponíveis',
    'dashboard.projects': 'Meus projetos',
    'dashboard.new_project': 'Novo projeto',
    'dashboard.no_projects': 'Sem projetos',
    'dashboard.create_first': 'Crie seu primeiro vídeo musical!',
    'dashboard.payment_success': 'Pagamento realizado com sucesso! Seus créditos foram adicionados.',
    
    // Project
    'project.title': 'Título do projeto',
    'project.language': 'Idioma',
    'project.format': 'Formato do vídeo',
    'project.format.landscape': 'Paisagem (16:9)',
    'project.format.portrait': 'Retrato (9:16)',
    'project.format.square': 'Quadrado (1:1)',
    'project.style': 'Estilo visual',
    'project.mood': 'Clima / Descrição',
    'project.audio': 'Arquivo de áudio',
    'project.srt': 'Legendas (opcional)',
    'project.intro': 'Intro do vídeo (opcional)',
    'project.outro': 'Outro do vídeo (opcional)',
    'project.photo': 'Foto do personagem (opcional)',
    'project.mode': 'Modo de criação',
    'project.mode.auto': 'Automático',
    'project.mode.manual': 'Manual (passo a passo)',
    'project.public': 'Publicar na galeria',
    'project.create': 'Criar projeto',
    'project.no_credits': 'Você não tem créditos. Por favor compre alguns para criar um projeto.',
    'project.error_no_audio': 'Por favor selecione um arquivo de áudio',
    'project.error_no_title': 'Por favor insira um título',
    'project.start': 'Iniciar geração',
    'project.cancel': 'Cancelar',
    'project.delete': 'Excluir',
    'project.download_assets': 'Baixar assets',
    'project.download_assets_desc': 'Roteiro, imagens e vídeos (ZIP)',
    'project.download_assets_success': 'Arquivo criado com sucesso',
    'project.download_assets_error': 'Erro ao criar o arquivo',
    'project.director_required': 'Assinatura Director necessária',
    'project.director_required_desc': 'Assine o Director para baixar todos os arquivos do projeto',
    'project.active_warning.title': 'Projeto em andamento',
    'project.active_warning.message': 'Você já tem um projeto em geração: "{title}". Você não pode iniciar um novo projeto até que este seja concluído.',
    'project.active_warning.view_project': 'Ver projeto atual',
    
    // Script Editor
    'script.title': 'Editor de roteiro',
    'script.scenes': 'cenas',
    'script.total_duration': 'duração total',
    'script.add_scene': 'Adicionar cena',
    'script.validate_and_generate': 'Validar e gerar imagens',
    'script.description': 'Descrição',
    'script.image_prompt': 'Prompt de imagem',
    'script.video_prompt': 'Prompt de vídeo',
    'script.duration_s': 'Duração (segundos)',
    'script.edit_scene': 'Editar cena',
    'script.description_placeholder': 'Descrição narrativa da cena...',
    'script.image_prompt_placeholder': 'Prompt detalhado para geração de imagem (em inglês)...',
    'script.video_prompt_placeholder': 'Prompt detalhado para animação de vídeo (em inglês)...',
    'script.scene_updated': 'Cena atualizada',
    'script.scene_added': 'Cena adicionada',
    'script.scene_deleted': 'Cena excluída',
    'script.error_update': 'Erro ao atualizar',
    'script.error_create': 'Erro ao criar',
    'script.fill_all_fields': 'Por favor preencha todos os campos obrigatórios',
    'script.confirm_delete_title': 'Excluir cena',
    'script.confirm_delete_message': 'Tem certeza de que deseja excluir esta cena?',
    'script.confirm_validate_title': 'Validar roteiro',
    'script.confirm_validate_message': 'Validar o roteiro iniciará a geração de imagens. Isso consumirá créditos. Continuar?',
    'script.validate': 'Validar',
    'script.add': 'Adicionar',
    'script.no_scenes': 'Nenhuma cena no roteiro',
    'script.awaiting_validation': 'O roteiro está pronto. Edite-o se necessário e depois valide para iniciar a geração de imagens.',
    
    // Image Validation
    'images.title': 'Validação de imagens',
    'images.images': 'imagens',
    'images.regenerations_left': 'regenerações restantes',
    'images.instructions': 'Clique em uma imagem para editá-la, regenerá-la ou substituí-la. Em seguida, valide para iniciar a geração de vídeos.',
    'images.click_to_edit': 'Clique para editar',
    'images.edit_image': 'Editar imagem',
    'images.no_image': 'Sem imagem',
    'images.regenerate': 'Regenerar',
    'images.remaining': 'restantes',
    'images.regeneration_limit_reached': 'Limite de regeneração atingido (máximo 3)',
    'images.prompt_required': 'O prompt da imagem (em inglês) é obrigatório',
    'images.upload_replacement': 'Enviar imagem',
    'images.upload_hint': 'Sem limite nos envios',
    'images.confirm_regenerate_title': 'Regenerar imagem',
    'images.confirm_regenerate_message': 'Deseja regenerar esta imagem? Você tem {remaining} regeneração(ões) restante(s).',
    'images.regeneration_started': 'Regeneração iniciada',
    'images.invalid_file_type': 'Tipo de arquivo inválido. Por favor envie uma imagem.',
    'images.file_too_large': 'Arquivo muito grande (máx 10 MB)',
    'images.upload_success': 'Imagem enviada com sucesso',
    'images.upload_error': 'Erro no envio',
    'images.confirm_validate_title': 'Validar imagens',
    'images.confirm_validate_message': 'Validar as imagens iniciará a geração de vídeos. Isso consumirá créditos. Continuar?',
    'images.validate_and_generate': 'Validar e gerar vídeos',
    'images.awaiting_validation': 'As imagens estão prontas. Revise e edite-as se necessário, depois valide para iniciar a geração de vídeos.',
    
    // Video Validation
    'videos.title': 'Validação de vídeos',
    'videos.videos': 'vídeos',
    'videos.regenerations_left': 'regenerações restantes',
    'videos.instructions': 'Clique em um vídeo para editá-lo ou regenerá-lo. Use as setas para reordenar as cenas. Em seguida, valide para iniciar a fusão final.',
    'videos.click_to_edit': 'Clique para editar',
    'videos.edit_video': 'Editar vídeo',
    'videos.no_video': 'Sem vídeo',
    'videos.regenerate': 'Regenerar',
    'videos.remaining': 'restantes',
    'videos.regeneration_limit_reached': 'Limite de regeneração atingido (máximo 3)',
    'videos.confirm_regenerate_title': 'Regenerar vídeo',
    'videos.confirm_regenerate_message': 'Deseja regenerar este vídeo? Você tem {remaining} regeneração(ões) restante(s).',
    'videos.regeneration_started': 'Regeneração iniciada',
    'videos.scene_number': 'Número da cena',
    'videos.duration': 'Duração',
    'videos.move_left': 'Mover para a esquerda',
    'videos.move_right': 'Mover para a direita',
    'videos.scenes_swapped': 'Cenas trocadas',
    'videos.confirm_validate_title': 'Validar vídeos',
    'videos.confirm_validate_message': 'Validar os vídeos iniciará a fusão final e adição de áudio. Continuar?',
    'videos.validate_and_merge': 'Validar e mesclar',
    'videos.awaiting_validation': 'Os vídeos estão prontos. Reordene se necessário, depois valide para iniciar a fusão final com áudio.',
    
    // Status (Project)
    'status.draft': 'Rascunho',
    'status.pending': 'Pendente',
    'status.processing': 'Processando',
    'status.script_ready': 'Roteiro pronto',
    'status.images_ready': 'Imagens prontas',
    'status.videos_ready': 'Vídeos prontos',
    'status.completed': 'Concluído',
    'status.failed': 'Falhou',
    'status.cancelled': 'Cancelado',
    // Status (Scene)
    'status.image_generating': 'Gerando imagem...',
    'status.image_ready': 'Imagem pronta',
    'status.video_generating': 'Gerando vídeo...',
    'status.video_ready': 'Vídeo pronto',
    
    // Wizard
    'wizard.step1': 'Roteiro',
    'wizard.step2': 'Imagens',
    'wizard.step3': 'Vídeos',
    'wizard.step4': 'Fusão',
    'wizard.step5': 'Áudio',
    'wizard.validate': 'Validar e continuar',
    'wizard.regenerate': 'Regenerar',
    'wizard.edit': 'Editar',
    
    // Auth
    'auth.login_with_google': 'Continuar com Google',
    'auth.login_with_facebook': 'Continuar com Facebook',
    'auth.or': 'ou',
    'auth.dev_login': 'Login de desenvolvedor',
    
    // Profile
    'profile.title': 'Meu perfil',
    'profile.display_name': 'Nome de exibição',
    'profile.language': 'Idioma',
    'profile.theme': 'Tema',
    'profile.theme.dark': 'Escuro',
    'profile.theme.light': 'Claro',
    'profile.save': 'Salvar',
    'profile.saved': 'Salvo',
    'profile.transactions': 'Histórico de transações',
    'profile.no_transactions': 'Sem transações',
    'profile.subscription.title': 'Assinatura Director',
    'profile.subscription.active': 'Assinatura ativa',
    'profile.subscription.canceling': 'Cancelamento agendado',
    'profile.subscription.renews_on': 'Renova em {date}',
    'profile.subscription.ends_on': 'Termina em {date}',
    'profile.subscription.cancel': 'Cancelar assinatura',
    'profile.subscription.cancel_title': 'Cancelar assinatura?',
    'profile.subscription.cancel_confirm': 'Tem certeza de que deseja cancelar sua assinatura Director? Você manterá o acesso até o final do período atual.',
    'profile.subscription.confirm_cancel': 'Sim, cancelar',
    'profile.subscription.cancelled': 'Assinatura cancelada',
    'profile.subscription.cancel_error': 'Erro ao cancelar a assinatura',
    'profile.subscription.reactivate': 'Reativar assinatura',
    'profile.subscription.reactivated': 'Assinatura reativada',
    'profile.subscription.reactivate_error': 'Erro ao reativar a assinatura',
    'profile.subscription.manage_payment': 'Gerenciar pagamento',
    'profile.subscription.portal_error': 'Erro ao acessar o portal',
    'profile.subscription.not_subscribed': 'Você ainda não assina o Director',
    'profile.subscription.subscribe_now': 'Assinar agora',
    'profile.subscription_success': 'Assinatura Director ativada com sucesso!',
    'profile.billing.title': 'Faturamento',
    'profile.billing.description': 'Acesse suas faturas, recibos de cartão e gerencie seus métodos de pagamento.',
    'profile.billing.access_portal': 'Minha conta Stripe',
    'profile.billing.portal_error': 'Não foi possível acessar o portal de faturamento',
    
    // Common
    'common.loading': 'Carregando...',
    'common.error': 'Ocorreu um erro',
    'common.retry': 'Tentar novamente',
    'common.save': 'Salvar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Excluir',
    'common.confirm': 'Confirmar',
    'common.back': 'Voltar',
    'common.next': 'Próximo',
    'common.download': 'Baixar',
    'common.share': 'Compartilhar',
    'common.close': 'Fechar',
    'common.edit': 'Editar',
    
    // Project View
    'project.not_found': 'Projeto não encontrado',
    'project.back_to_dashboard': 'Voltar ao painel',
    'project.processing': 'Processando... {progress}%',
    'project.your_video': 'Seu vídeo',
    'project.scenes_count': 'Cenas ({count})',
    'project.scene_number': 'Cena {number}',
    'project.confirm_delete_title': 'Confirmar exclusão',
    'project.confirm_delete_message': 'Tem certeza de que deseja excluir este projeto? Esta ação é irreversível.',
    // Project Create
    'project.create_subtitle': 'Crie seu vídeo musical em poucos cliques',
    'project.drop_audio': 'Arraste seu arquivo de áudio aqui',
    'project.audio_formats': 'MP3, WAV, OGG...',
    'project.section_info': 'Informações',
    'project.section_mode': 'Modo de criação',
    'project.section_files': 'Arquivos opcionais',
    'project.mode.auto_desc': 'A IA gera tudo automaticamente',
    'project.mode.manual_desc': 'Controle cada etapa da criação',
    'project.add_subtitles': 'Adicionar legendas',
    'project.add_photo': 'Adicionar foto de personagem',
    'project.add_intro': 'Adicionar vídeo de intro',
    'project.add_outro': 'Adicionar vídeo de outro',
    'project.available_credits': 'Créditos disponíveis',
    
    // Profile
    'profile.section_info': 'Informações',
  }
}

// Current locale
const currentLocale = ref<Locale>('fr')

// Get translation
export function t(key: string, params?: Record<string, string | number>): string {
  const locale = currentLocale.value
  let text = translations[locale]?.[key] || translations['en']?.[key] || key
  
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      text = text.replace(`{${k}}`, String(v))
    })
  }
  
  return text
}

// Set locale
export function setLocale(locale: Locale) {
  currentLocale.value = locale
  localStorage.setItem('m2v_locale', locale)
  document.documentElement.lang = locale
}

// Get current locale
export function getLocale(): Locale {
  return currentLocale.value
}

// Initialize locale from storage
export function initLocale() {
  const stored = localStorage.getItem('m2v_locale') as Locale
  if (stored && LOCALES[stored]) {
    currentLocale.value = stored
  } else {
    // Try to detect browser language
    const browserLang = navigator.language.split('-')[0] as Locale
    if (LOCALES[browserLang]) {
      currentLocale.value = browserLang
    }
  }
  document.documentElement.lang = currentLocale.value
}

// Composable
export function useI18n() {
  return {
    t,
    locale: computed(() => currentLocale.value),
    setLocale,
    locales: LOCALES
  }
}
